import { PLANTS, type Plant, type PlantNativeRange, type PlantNativeRangeRegion } from '@/data/plants';
import {
  nativeRangeCanMatchSubdivision,
  nativeRangeHasDisplayableData,
  nativeRangeHasNativeSubdivisionRows,
} from '@/lib/nativeRange';

type SourceReliability = 'authoritative' | 'secondary' | 'unknown';
type MatchType = 'scientific-name' | 'common-name';
type CoverageLevel = 'complete' | 'partial' | 'broad-only';
type DistributionComplexity = 'straight-species' | 'domesticate' | 'hybrid' | 'aggregate' | 'cultivar-group' | 'uncertain';

export interface NativeRangeLookupResult {
  found: boolean;
  source?: string;
  sourceNotes?: string[];
  matchType?: MatchType;
  sourceReliability?: SourceReliability;
  coverageLevel?: CoverageLevel;
  distributionComplexity?: DistributionComplexity;
  regions?: PlantNativeRangeRegion[];
  broaderNativeRange?: string[];
  origin?: string;
  nativeHabitat?: string;
  currentDistribution?: string;
  reason?: string;
}

export interface NativeRangeBackfillResult {
  plantId: string;
  plantName: string;
  status: 'skipped_reviewed' | 'imported' | 'failed' | 'skipped_existing_import';
  importSource?: string;
  importConfidence?: PlantNativeRange['importConfidence'];
  reason?: string;
  plant: Plant;
}

export interface NativeRangeCoverageReport {
  totalPlants: number;
  reviewed: number;
  manually_reviewed_exact_match_eligible: number;
  auto_imported_high_confidence: number;
  auto_imported_high_confidence_exact_match_eligible: number;
  auto_imported_medium_confidence: number;
  auto_imported_low_confidence: number;
  auto_imported_display_only: number;
  failed_or_missing: number;
  intentionally_excluded: number;
}

export interface NativeRangeSourceRecord {
  scientificName: string;
  commonNames: string[];
  source: string;
  sourceNotes: string[];
  sourceReliability: SourceReliability;
  coverageLevel: CoverageLevel;
  distributionComplexity?: DistributionComplexity;
  regions: PlantNativeRangeRegion[];
  broaderNativeRange?: string[];
  origin?: string;
  nativeHabitat?: string;
  currentDistribution?: string;
}

function broadNativeRangeRecord({
  scientificName,
  commonNames,
  broaderNativeRange,
  origin,
  nativeHabitat,
  currentDistribution,
  source = 'POWO',
  sourceNotes,
  distributionComplexity = 'straight-species',
}: {
  scientificName: string;
  commonNames: string[];
  broaderNativeRange: string[];
  origin: string;
  nativeHabitat?: string;
  currentDistribution: string;
  source?: string;
  sourceNotes?: string[];
  distributionComplexity?: DistributionComplexity;
}): NativeRangeSourceRecord {
  return {
    scientificName,
    commonNames,
    source,
    sourceNotes: sourceNotes ?? [
      `Added as broad native-range context from ${source}; state/province distribution details are omitted until authoritative local distribution data is available.`,
    ],
    sourceReliability: 'authoritative',
    coverageLevel: 'broad-only',
    distributionComplexity,
    regions: [],
    broaderNativeRange,
    origin,
    ...(nativeHabitat ? { nativeHabitat } : {}),
    currentDistribution,
  };
}

function exactSubdivisionRangeRecord({
  scientificName,
  commonNames,
  source,
  sourceNotes,
  regions,
  broaderNativeRange,
  origin,
  nativeHabitat,
  currentDistribution,
  distributionComplexity = 'straight-species',
}: {
  scientificName: string;
  commonNames: string[];
  source: string;
  sourceNotes: string[];
  regions: PlantNativeRangeRegion[];
  broaderNativeRange: string[];
  origin: string;
  nativeHabitat?: string;
  currentDistribution: string;
  distributionComplexity?: DistributionComplexity;
}): NativeRangeSourceRecord {
  return {
    scientificName,
    commonNames,
    source,
    sourceNotes,
    sourceReliability: 'authoritative',
    coverageLevel: 'partial',
    distributionComplexity,
    regions,
    broaderNativeRange,
    origin,
    ...(nativeHabitat ? { nativeHabitat } : {}),
    currentDistribution,
  };
}

const AUTHORITATIVE_EXACT_SUBDIVISION_IMPORTS: NativeRangeSourceRecord[] = [
  exactSubdivisionRangeRecord({
    scientificName: 'Vaccinium corymbosum',
    commonNames: ['blueberry', 'highbush blueberry'],
    source: 'USDA FEIS',
    sourceNotes: [
      'Imported from USDA Forest Service FEIS highbush blueberry distribution notes; commercial/introduction-only areas are kept out of native matches.',
    ],
    regions: [
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'US-WA', status: 'introduced', note: 'Cultivated and introduced outside the natural range.' },
      { code: 'US-WI', status: 'introduced', note: 'Cultivated and introduced outside the natural range.' },
      { code: 'CA-BC', status: 'introduced', note: 'Commercial/introduction record, not treated as native for matching.' },
      { code: 'CA-NB', status: 'introduced', note: 'Commercial/introduction record, not treated as native for matching.' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Highbush blueberry is native to eastern North America, from the Great Lakes and Atlantic Canada south through the eastern United States to Florida and eastern Texas.',
    nativeHabitat: 'Acidic wetlands, bog margins, pond and stream edges, moist open woods, pine barrens, and other low-pH sites with steady moisture.',
    currentDistribution: 'Occurs naturally across eastern North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Rubus idaeus',
    commonNames: ['raspberry', 'red raspberry', 'american red raspberry'],
    source: 'USDA FEIS',
    sourceNotes: [
      'Imported from USDA Forest Service FEIS American red raspberry distribution notes; rows refer to native North American red raspberry populations, with cultivated and Eurasian lineages treated cautiously.',
    ],
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-AK', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' },
      { code: 'US-CA', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-HI', status: 'introduced', note: 'Cultivated/naturalized record, not treated as native for matching.' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' },
      { code: 'US-NV', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['North America', 'Cool temperate Northern Hemisphere'],
    origin: 'North American red raspberry is native across much of Canada and the United States, while Eurasian Rubus idaeus lineages and garden cultivars complicate exact cultivar provenance.',
    nativeHabitat: 'Forest clearings, burned or cut-over woods, old fields, ravines, bluffs, streambanks, talus slopes, and other light-gap habitats with adequate moisture.',
    currentDistribution: 'Occurs naturally across much of North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Helianthus annuus',
    commonNames: ['sunflower', 'common sunflower'],
    source: 'POWO',
    sourceNotes: [
      'Imported from Kew Plants of the World Online exact native distribution for wild Helianthus annuus; broader North American occurrence includes cultivated and escaped populations.',
    ],
    regions: [
      { code: 'US-AZ', status: 'native' },
      { code: 'US-CA', status: 'native' },
      { code: 'US-NV', status: 'native' },
    ],
    broaderNativeRange: ['Southwestern United States', 'Mexico'],
    origin: 'Wild common sunflower is native to parts of the southwestern United States and Mexico, while the crop was domesticated in North America and later spread worldwide.',
    nativeHabitat: 'Open sunny ground, prairie and grassland edges, old fields, roadsides, rail corridors, savannas, and other disturbed sites with exposed mineral soil.',
    currentDistribution: 'Occurs naturally in parts of the southwestern United States and Mexico; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Achillea millefolium',
    commonNames: ['yarrow', 'common yarrow'],
    source: 'Native Plant Trust; Calflora',
    sourceNotes: [
      'Native Plant Trust New England Achillea millefolium ssp. lanulosa records and Calflora California status are used for named state records; garden strains may not be local ecotypes.',
    ],
    regions: [
      { code: 'US-CA', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-VT', status: 'native' },
    ],
    broaderNativeRange: ['Temperate Northern Hemisphere'],
    origin: 'Yarrow is a circumboreal species complex native across much of the temperate Northern Hemisphere, with North American and Eurasian lineages that are not always simple to separate in garden plants.',
    nativeHabitat: 'Dry meadows, grasslands, open woods, prairie edges, pastures, and disturbed sunny ground with lean, well-drained soil.',
    currentDistribution: 'Widespread across temperate regions as both native populations and introduced or naturalized forms; garden seed strains may not represent local native ecotypes.',
    distributionComplexity: 'uncertain',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Cucurbita pepo',
    commonNames: ['zucchini', 'summer squash', 'pumpkin'],
    source: 'Native Plant Trust; POWO',
    sourceNotes: [
      'Imported exact structured occurrence rows for Cucurbita pepo, but kept low confidence for local native matching because the garden listing is a cultivated summer-squash form and wild native Cucurbita pepo lineages are taxonomically narrower than ordinary zucchini.',
    ],
    regions: [
      { code: 'US-CT', status: 'introduced' },
      { code: 'US-MA', status: 'introduced' },
      { code: 'US-NH', status: 'introduced' },
      { code: 'US-VT', status: 'introduced' },
    ],
    broaderNativeRange: ['Mexico', 'Southern United States'],
    origin: 'Cucurbita pepo includes wild and domesticated North American squash lineages, but zucchini is a cultivated garden form rather than a local wild ecotype.',
    nativeHabitat: 'Wild forms occupy river bottoms, open thickets, disturbed alluvial soils, and warm-season margins with room for sprawling vines.',
    currentDistribution: 'Widely cultivated in warm garden regions; not native outside its region of origin.',
    distributionComplexity: 'cultivar-group',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Mentha spicata',
    commonNames: ['mint', 'spearmint'],
    source: 'POWO; Native Plant Trust',
    sourceNotes: [
      'Kew Plants of the World Online and Native Plant Trust list North American introduced occurrence; spearmint is not native to North America.',
    ],
    regions: [
      { code: 'US-CT', status: 'introduced' },
      { code: 'US-MA', status: 'introduced' },
      { code: 'US-ME', status: 'introduced' },
      { code: 'US-NH', status: 'introduced' },
      { code: 'US-RI', status: 'introduced' },
      { code: 'US-VT', status: 'introduced' },
      { code: 'CA-AB', status: 'introduced' },
      { code: 'CA-BC', status: 'introduced' },
      { code: 'CA-MB', status: 'introduced' },
      { code: 'CA-NB', status: 'introduced' },
      { code: 'CA-NS', status: 'introduced' },
      { code: 'CA-ON', status: 'introduced' },
      { code: 'CA-PE', status: 'introduced' },
      { code: 'CA-QC', status: 'introduced' },
      { code: 'CA-SK', status: 'introduced' },
    ],
    broaderNativeRange: ['Europe', 'Western Asia'],
    origin: 'Spearmint is native to Europe and western Asia, with a long history of cultivation and naturalization beyond that range.',
    nativeHabitat: 'Damp meadows, stream margins, ditches, wet grasslands, and open moist soils.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Tagetes patula',
    commonNames: ['marigold', 'french marigold'],
    source: 'Native Plant Trust',
    sourceNotes: [
      'Imported exact New England occurrence from Native Plant Trust; French marigold is non-native in North America.',
    ],
    regions: [
      { code: 'US-VT', status: 'introduced' },
    ],
    broaderNativeRange: ['Mexico', 'Central America'],
    origin: 'French marigold is native to Mexico and Central America.',
    nativeHabitat: 'Open sunny slopes, disturbed ground, field margins, and warm seasonal habitats.',
    currentDistribution: 'Widely cultivated in gardens worldwide; not native outside its region of origin.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Ipomoea purpurea',
    commonNames: ['morning glory', 'common morning glory'],
    source: 'Native Plant Trust',
    sourceNotes: [
      'Imported exact New England occurrence from Native Plant Trust; common morning glory is not native to North America.',
    ],
    regions: [
      { code: 'US-CT', status: 'introduced' },
      { code: 'US-MA', status: 'introduced' },
      { code: 'US-ME', status: 'introduced' },
      { code: 'US-NH', status: 'introduced' },
      { code: 'US-RI', status: 'introduced' },
      { code: 'US-VT', status: 'introduced' },
    ],
    broaderNativeRange: ['Mexico', 'Central America'],
    origin: 'Common morning glory is native to Mexico and Central America.',
    nativeHabitat: 'Warm open ground, thickets, field margins, roadsides, and disturbed soils where vines can climb surrounding vegetation.',
    currentDistribution: 'Naturalized across many warm and temperate regions, especially in disturbed habitats.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Baptisia australis',
    commonNames: ['baptisia', 'blue wild indigo', 'blue false indigo'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state distribution records for Baptisia australis.'],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and Central North America'],
    origin: 'Blue wild indigo is native to eastern and central North America, from the mid-Atlantic and Great Lakes regions south through the Appalachians into the central states.',
    nativeHabitat: 'Open woods, prairies, dry woodland edges, roadsides, and well-drained upland soils.',
    currentDistribution: 'Occurs naturally across eastern and central North America; also widely cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Salvia azurea',
    commonNames: ['blue pitcher sage', 'azure sage', 'azure blue sage', 'pitcher sage'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state distribution records for Salvia azurea.'],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
    ],
    broaderNativeRange: ['Central and Eastern United States'],
    origin: 'Blue pitcher sage is native to the central and eastern United States, with its core range in tallgrass prairie and savanna regions from the Great Plains east through the Appalachians.',
    nativeHabitat: 'Dry prairies, open glades, rocky hillsides, roadsides, and well-drained open ground.',
    currentDistribution: 'Occurs naturally across central and eastern North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Asimina triloba',
    commonNames: ['pawpaw', 'paw paw', 'common pawpaw'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state distribution records for Asimina triloba.'],
    regions: [
      { code: 'CA-ON', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Paw paw is native to eastern North America, primarily in the river bottoms and rich bottomland forests of the eastern and central United States.',
    nativeHabitat: 'Rich bottomland forests, river floodplains, moist ravines, and shaded streambanks with fertile, well-drained alluvial soils.',
    currentDistribution: 'Occurs naturally across eastern and central North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Diospyros virginiana',
    commonNames: ['persimmon', 'american persimmon', 'common persimmon'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state distribution records for Diospyros virginiana.'],
    regions: [
      { code: 'CA-ON', status: 'introduced' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'uncertain', note: 'Status uncertain; may be naturalized rather than strictly native at the northeastern edge of range.' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and Central United States'],
    origin: 'American persimmon is native to the eastern and central United States, ranging from the Atlantic seaboard west through the Ozarks and into the southern Great Plains.',
    nativeHabitat: 'Open woods, thickets, old fields, roadsides, floodplain margins, and forest edges on moderately dry to moist upland soils.',
    currentDistribution: 'Occurs naturally across eastern and central North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Morus rubra',
    commonNames: ['mulberry', 'red mulberry'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state distribution records for Morus rubra; white mulberry (Morus alba) is a separate introduced species.'],
    regions: [
      { code: 'CA-ON', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Red mulberry is native to eastern North America, from the Atlantic seaboard west to the Great Plains and south to Florida and Texas.',
    nativeHabitat: 'Rich bottomland forests, moist floodplains, woodland edges, and disturbed openings with fertile soil.',
    currentDistribution: 'Occurs naturally across eastern North America; hybridizes with introduced white mulberry (Morus alba) in some regions.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Ribes americanum',
    commonNames: ['currant', 'american black currant', 'wild black currant'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Ribes americanum.'],
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-NT', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['Northern North America'],
    origin: 'American black currant is native to much of northern North America, from the Great Plains and upper Midwest east through New England and north into the Canadian boreal and prairie provinces.',
    nativeHabitat: 'Moist woodland edges, thickets, streambanks, and rich open habitats with moderate to high moisture.',
    currentDistribution: 'Occurs naturally across northern North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Eutrochium purpureum',
    commonNames: ['joe-pye weed', 'sweet joe-pye weed', 'purple joe-pye weed'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Eutrochium purpureum (formerly Eupatorium purpureum).'],
    regions: [
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Joe-pye weed is native to eastern North America, from the Atlantic seaboard west through the Great Lakes and Mississippi Valley.',
    nativeHabitat: 'Moist meadows, thickets, woodland edges, stream margins, and wet open ground.',
    currentDistribution: 'Occurs naturally across eastern North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Solidago canadensis',
    commonNames: ['goldenrod', 'canada goldenrod', 'common goldenrod'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Solidago canadensis; this species complex is native across much of North America.'],
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-NT', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'CA-YT', status: 'native' },
      { code: 'US-AK', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' },
      { code: 'US-CA', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['North America'],
    origin: 'Canada goldenrod is native across most of North America, from Alaska and northern Canada south through the contiguous United States.',
    nativeHabitat: 'Meadows, prairies, old fields, roadsides, woodland edges, and disturbed open ground across a wide range of soil types.',
    currentDistribution: 'Occurs naturally across North America; also naturalized in Europe and Asia where introduced as an ornamental.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Lobelia cardinalis',
    commonNames: ['cardinal flower', 'red lobelia'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Lobelia cardinalis.'],
    regions: [
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Southwestern United States'],
    origin: 'Cardinal flower is native to eastern North America and parts of the Southwest, where it grows along stream corridors and moist lowlands from Canada to the Gulf Coast.',
    nativeHabitat: 'Stream banks, wet meadows, pond and lake margins, moist woodland edges, and shaded ravine bottoms.',
    currentDistribution: 'Occurs naturally across eastern and central North America and parts of the Southwest; also widely cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Asclepias incarnata',
    commonNames: ['swamp milkweed', 'pink milkweed', 'rose milkweed'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Asclepias incarnata.'],
    regions: [
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and Central North America'],
    origin: 'Swamp milkweed is native to eastern and central North America, from the Atlantic seaboard west to the Great Plains and north into southern Canada.',
    nativeHabitat: 'Wet meadows, marshes, pond margins, stream banks, wet roadsides, and other consistently moist to wet open habitats.',
    currentDistribution: 'Occurs naturally across eastern and central North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Asarum canadense',
    commonNames: ['wild ginger', 'canadian wild ginger'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Asarum canadense.'],
    regions: [
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Wild ginger is native to eastern North America, occurring in rich moist woodlands from the Great Plains east to the Atlantic and north into southern Canada.',
    nativeHabitat: 'Rich deciduous woodlands, moist shaded ravines, north-facing slopes, and floodplain forests with deep, humus-rich soil.',
    currentDistribution: 'Occurs naturally across eastern North America; also cultivated as a shade groundcover beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Monarda fistulosa',
    commonNames: ['wild bergamot', 'bee balm', 'bergamot'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Monarda fistulosa; this is the prairie and dry-woods bee balm, distinct from scarlet bee balm (Monarda didyma).'],
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['North America'],
    origin: 'Wild bergamot is native across most of North America, one of the most widespread native bee balms, ranging from coast to coast in the United States and across much of Canada.',
    nativeHabitat: 'Dry to mesic prairies, open woods, roadsides, thickets, and disturbed sunny ground across a wide range of soil types.',
    currentDistribution: 'Occurs naturally across most of North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Aronia melanocarpa',
    commonNames: ['aronia', 'black chokeberry'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Aronia melanocarpa.'],
    regions: [
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Black chokeberry is native to eastern North America, from the Atlantic coast west through the Great Lakes and upper Midwest.',
    nativeHabitat: 'Bogs, wet thickets, swamp margins, moist woodland edges, and rocky open slopes with adequate moisture.',
    currentDistribution: 'Occurs naturally across eastern North America; widely cultivated for its high-antioxidant berries beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Aquilegia canadensis',
    commonNames: ['wild columbine', 'canadian columbine', 'eastern red columbine'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Aquilegia canadensis.'],
    regions: [
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Wild columbine is native to eastern North America, occurring from the southern Canadian provinces south through the eastern United States into the Gulf states.',
    nativeHabitat: 'Rocky woodland slopes, ledges, cliffs, open woods, and shaded ravines, especially on well-drained rocky or sandy soils.',
    currentDistribution: 'Occurs naturally across eastern North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Veronicastrum virginicum',
    commonNames: ["culver's root", 'culverroot', 'blackroot'],
    source: 'USDA PLANTS',
    sourceNotes: ["Imported from USDA PLANTS state and province distribution records for Veronicastrum virginicum."],
    regions: [
      { code: 'CA-ON', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: "Culver's root is native to eastern and central North America, from the Great Plains east through the Appalachians and north into Ontario.",
    nativeHabitat: 'Moist prairies, wet meadows, open woodland edges, thickets, and streamside habitats with moderate to good moisture.',
    currentDistribution: "Occurs naturally across eastern and central North America; also cultivated beyond its native range.",
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Penstemon digitalis',
    commonNames: ['penstemon', 'foxglove beardtongue', 'white beardtongue'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state distribution records for Penstemon digitalis.'],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and Central United States'],
    origin: 'Foxglove beardtongue is native to the eastern and central United States, from the Great Plains east to the Atlantic seaboard.',
    nativeHabitat: 'Moist meadows, open woodland edges, roadsides, prairies, and disturbed open ground with moderate moisture.',
    currentDistribution: 'Occurs naturally across eastern and central North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Tradescantia ohiensis',
    commonNames: ['spiderwort', 'ohio spiderwort', 'bluejacket'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state distribution records for Tradescantia ohiensis.'],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and Central United States'],
    origin: 'Ohio spiderwort is native to the eastern and central United States, from the Great Plains east to New England and south to Florida and Texas.',
    nativeHabitat: 'Moist to dry roadsides, prairies, open woods, thickets, and disturbed sandy or rocky open ground.',
    currentDistribution: 'Occurs naturally across eastern and central North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Cornus florida',
    commonNames: ['flowering dogwood', 'eastern flowering dogwood'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Cornus florida.'],
    regions: [
      { code: 'CA-ON', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Flowering dogwood is native to eastern North America, one of the most iconic understory trees of eastern deciduous forests.',
    nativeHabitat: 'Understory of deciduous and mixed forests, woodland edges, ravine slopes, and well-drained upland sites in partial to full shade.',
    currentDistribution: 'Occurs naturally across eastern North America; also widely cultivated as an ornamental well beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Hamamelis virginiana',
    commonNames: ['witch hazel', 'american witch hazel', 'virginia witch hazel'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Hamamelis virginiana.'],
    regions: [
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Witch hazel is native to eastern North America, from Nova Scotia and Ontario south through the eastern United States to Florida and Texas.',
    nativeHabitat: 'Moist deciduous woodlands, forest edges, streambanks, ravine slopes, and understory habitats on well-drained to moderately moist soils.',
    currentDistribution: 'Occurs naturally across eastern North America; also widely cultivated as a late-season ornamental beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Passiflora incarnata',
    commonNames: ['maypop', 'purple passionflower', 'wild passion vine'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state distribution records for Passiflora incarnata.'],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and Southeastern United States'],
    origin: 'Maypop is native to the eastern and southeastern United States, from the Atlantic seaboard west to Kansas and Oklahoma.',
    nativeHabitat: 'Roadsides, thickets, old fields, open woodland edges, and disturbed sunny ground with well-drained soil.',
    currentDistribution: 'Occurs naturally across eastern and southeastern North America; also cultivated beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Agastache foeniculum',
    commonNames: ['anise hyssop', 'blue giant hyssop', 'fragrant hyssop'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Agastache foeniculum.'],
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['Central North America', 'Northern Great Plains'],
    origin: 'Anise hyssop is native to the central and northern Great Plains and prairie provinces of North America, from the upper Midwest through the Canadian prairies.',
    nativeHabitat: 'Dry prairies, open woods, rocky slopes, and disturbed sunny ground on well-drained soils.',
    currentDistribution: 'Occurs naturally across the central and northern Great Plains; also widely cultivated as a pollinator plant and culinary herb beyond its native range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Lonicera caerulea',
    commonNames: ['honeyberry', 'blue honeysuckle', 'haskap'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'Imported from USDA PLANTS state and province distribution records for Lonicera caerulea; North American occurrences represent native boreal and montane subspecies distinct from Asian cultivated haskaps.',
    ],
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-NT', status: 'native' },
      { code: 'CA-NU', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'CA-YT', status: 'native' },
      { code: 'US-AK', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['Boreal North America', 'Northern Asia', 'Circumboreal'],
    origin: 'Blue honeysuckle is a circumboreal species with native North American subspecies in boreal forests and montane regions of northern Canada, Alaska, and the northern United States.',
    nativeHabitat: 'Boreal forests, wetland edges, alder and willow thickets, cool montane slopes, and moist open ground in northern and high-elevation regions.',
    currentDistribution: 'Occurs naturally in boreal North America and northern Asia; also widely cultivated as haskap berry across much of the temperate world.',
    distributionComplexity: 'aggregate',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Helianthus tuberosus',
    commonNames: ['jerusalem artichoke', 'sunchoke', 'topinambur'],
    source: 'USDA PLANTS',
    sourceNotes: ['Imported from USDA PLANTS state and province distribution records for Helianthus tuberosus; native to central and eastern North America, introduced broadly elsewhere.'],
    regions: [
      { code: 'CA-AB', status: 'introduced', note: 'Naturalized from introduction; not part of the original native range.' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'introduced', note: 'Naturalized from introduction or spread from cultivation.' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['Central and Eastern North America'],
    origin: 'Jerusalem artichoke is native to central and eastern North America, where it was cultivated and spread across a broad range by Indigenous peoples well before European contact.',
    nativeHabitat: 'Disturbed soils, woodland edges, floodplain margins, and prairie openings; a pioneer species that colonizes open sunny ground with moderate moisture.',
    currentDistribution: 'Occurs naturally across central and eastern North America; widely naturalized as a garden escape across Europe, Asia, and Australia.',
  }),
];

const AUTHORITATIVE_NATIVE_RANGE_IMPORTS: NativeRangeSourceRecord[] = [
  {
    scientificName: 'Solanum lycopersicum',
    commonNames: ['tomato'],
    source: 'POWO',
    sourceNotes: [
      'Added as broad native-range context from Kew Plants of the World Online; North American local nativity is not represented for this domesticated crop.',
    ],
    sourceReliability: 'authoritative',
    coverageLevel: 'broad-only',
    regions: [],
    broaderNativeRange: ['Western South America'],
    origin: 'Tomato is derived from wild Solanum lineages native to western South America, with domestication and crop development involving Andean and Mesoamerican movement.',
    nativeHabitat: 'Wild relatives occur in dry valleys, coastal slopes, disturbed ground, and seasonally arid habitats rather than temperate North American garden conditions.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
  },
  {
    scientificName: 'Allium sativum',
    commonNames: ['garlic'],
    source: 'POWO',
    sourceNotes: [
      'Added as broad native-range context from Kew Plants of the World Online; cultivated garlic has a long domesticated history outside North American native floras.',
    ],
    sourceReliability: 'authoritative',
    coverageLevel: 'broad-only',
    regions: [],
    broaderNativeRange: ['Central Asia', 'Northeastern Iran region'],
    origin: 'Garlic is an Old World domesticate associated with Central Asian wild Allium ancestry and ancient cultivation across western Asia and the Mediterranean.',
    nativeHabitat: 'Wild relatives occupy dry, open, rocky slopes and steppe-like habitats with strong seasonal dormancy.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
  },
  {
    scientificName: 'Ocimum basilicum',
    commonNames: ['basil', 'sweet basil'],
    source: 'POWO',
    sourceNotes: [
      'Added as broad native-range context from Kew Plants of the World Online; North American local nativity is not represented.',
    ],
    sourceReliability: 'authoritative',
    coverageLevel: 'broad-only',
    regions: [],
    broaderNativeRange: ['Tropical and subtropical Asia', 'Africa'],
    origin: 'Sweet basil is an Old World herb with native and early cultivated associations in tropical Asia and Africa, followed by long movement through Mediterranean and global cuisines.',
    nativeHabitat: 'Warm open ground, seasonal margins, and disturbed tropical to subtropical habitats where frost is absent.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
  },
  broadNativeRangeRecord({
    scientificName: 'Capsicum annuum',
    commonNames: ['bell pepper', 'jalapeno pepper', 'pepper', 'chile pepper'],
    broaderNativeRange: ['Mexico', 'Central America', 'Northern South America'],
    origin: 'Capsicum annuum is native to the tropical and subtropical Americas, with domestication centered in Mexico and long movement through Indigenous American agriculture.',
    nativeHabitat: 'Wild and semi-wild peppers occur in warm open woodland edges, thickets, disturbed soils, and frost-free seasonal habitats.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Cucumis sativus',
    commonNames: ['cucumber'],
    broaderNativeRange: ['Indian subcontinent', 'Himalayan region'],
    origin: 'Cucumber is an Old World domesticate derived from Cucumis lineages native to the Indian subcontinent and Himalayan foothills.',
    nativeHabitat: 'Wild relatives grow as warm-season vines in open scrub, forest edges, and disturbed tropical to subtropical habitats.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Cucurbita pepo',
    commonNames: ['zucchini', 'summer squash', 'pumpkin'],
    broaderNativeRange: ['Mexico', 'Southern United States'],
    origin: 'Cucurbita pepo is native to North America, with wild relatives and domestication histories centered in Mexico and the southern United States.',
    nativeHabitat: 'Wild forms occupy river bottoms, open thickets, disturbed alluvial soils, and warm-season margins with room for sprawling vines.',
    currentDistribution: 'Widely cultivated in warm garden regions; not native outside its region of origin.',
    distributionComplexity: 'cultivar-group',
  }),
  broadNativeRangeRecord({
    scientificName: 'Cucurbita moschata',
    commonNames: ['butternut squash', 'winter squash'],
    broaderNativeRange: ['Tropical America', 'Central America', 'Northern South America'],
    origin: 'Cucurbita moschata is a tropical American squash domesticate with wild ancestry and cultivation history centered in warm regions of the Americas.',
    nativeHabitat: 'Wild and feral relatives favor warm open ground, disturbed soils, field margins, and seasonally moist tropical habitats.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Lactuca sativa',
    commonNames: ['lettuce'],
    broaderNativeRange: ['Eastern Mediterranean', 'Western Asia'],
    origin: 'Lettuce is an Old World crop derived from wild Lactuca lineages native to the Mediterranean basin and western Asia.',
    nativeHabitat: 'Wild relatives occur in sunny disturbed ground, rocky slopes, field edges, and seasonally dry open habitats.',
    currentDistribution: 'Widely cultivated in cool-season growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Brassica oleracea var. sabellica',
    commonNames: ['kale'],
    broaderNativeRange: ['Western Europe', 'Mediterranean Europe'],
    origin: 'Kale is a cultivated form of Brassica oleracea, whose wild coastal ancestors are native to parts of western and Mediterranean Europe.',
    nativeHabitat: 'Wild Brassica oleracea occupies maritime cliffs, coastal grasslands, rocky calcareous soils, and salt-exposed open habitats.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'cultivar-group',
  }),
  broadNativeRangeRecord({
    scientificName: 'Daucus carota',
    commonNames: ['carrot'],
    broaderNativeRange: ['Europe', 'North Africa', 'Western Asia'],
    origin: 'Carrot is derived from Daucus carota, a wild species native across Europe, North Africa, and western Asia.',
    nativeHabitat: 'Wild carrot grows in dry grasslands, field edges, roadsides, sandy soils, and other open disturbed habitats.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Allium cepa',
    commonNames: ['onion'],
    broaderNativeRange: ['Central Asia', 'Southwestern Asia'],
    origin: 'Onion is an ancient Allium domesticate associated with Central and southwestern Asian wild ancestry.',
    nativeHabitat: 'Wild Allium relatives grow in dry open slopes, rocky ground, and steppe-like habitats with pronounced seasonal dormancy.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Spinacia oleracea',
    commonNames: ['spinach'],
    broaderNativeRange: ['Southwestern Asia', 'Central Asia'],
    origin: 'Spinach is an Old World leafy crop native to southwestern and central Asia, with early cultivation associated with Persia and surrounding regions.',
    nativeHabitat: 'Wild and weedy relatives occur in cool-season open ground, saline or disturbed soils, and seasonally moist habitats.',
    currentDistribution: 'Widely cultivated in cool-season growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Brassica oleracea var. italica',
    commonNames: ['broccoli'],
    broaderNativeRange: ['Western Europe', 'Mediterranean Europe'],
    origin: 'Broccoli is a cultivated form of Brassica oleracea, a species with wild coastal ancestry in western and Mediterranean Europe.',
    nativeHabitat: 'Wild Brassica oleracea grows on maritime cliffs, coastal limestone, rocky slopes, and open salt-exposed habitats.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'cultivar-group',
  }),
  broadNativeRangeRecord({
    scientificName: 'Brassica oleracea var. capitata',
    commonNames: ['cabbage'],
    broaderNativeRange: ['Western Europe', 'Mediterranean Europe'],
    origin: 'Cabbage is a cultivated head-form of Brassica oleracea, whose wild ancestors are native to coastal western and Mediterranean Europe.',
    nativeHabitat: 'Wild Brassica oleracea favors open maritime cliffs, rocky coastal soils, and calcareous seaside grasslands.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'cultivar-group',
  }),
  broadNativeRangeRecord({
    scientificName: 'Beta vulgaris',
    commonNames: ['beet', 'beetroot', 'swiss chard'],
    broaderNativeRange: ['Mediterranean Basin', 'Western Europe', 'Western Asia'],
    origin: 'Beet and chard descend from Beta vulgaris, whose wild sea beet relatives are native around Mediterranean, Atlantic European, and western Asian coasts.',
    nativeHabitat: 'Wild beet grows on coastal shingle, salt-influenced margins, cliffs, and disturbed maritime soils.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Raphanus sativus',
    commonNames: ['radish'],
    broaderNativeRange: ['Eastern Mediterranean', 'Western Asia'],
    origin: 'Radish is an Old World domesticate associated with wild Raphanus ancestry in the eastern Mediterranean and western Asia.',
    nativeHabitat: 'Wild relatives occur in coastal ground, field edges, disturbed soils, and seasonally dry open habitats.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Ipomoea batatas',
    commonNames: ['sweet potato'],
    broaderNativeRange: ['Tropical America'],
    origin: 'Sweet potato is a tropical American domesticate of Ipomoea, with wild ancestry centered in the Americas.',
    nativeHabitat: 'Wild relatives are twining vines of warm open ground, thickets, field margins, and frost-free disturbed habitats.',
    currentDistribution: 'Widely cultivated in warm climates; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Zea mays',
    commonNames: ['corn', 'maize'],
    broaderNativeRange: ['Mexico', 'Mesoamerica'],
    origin: 'Maize is a Mesoamerican domesticate derived from teosinte lineages native to Mexico.',
    nativeHabitat: 'Wild teosinte relatives occupy warm open slopes, field margins, grasslands, and seasonally disturbed habitats.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Phaseolus vulgaris',
    commonNames: ['green beans', 'common bean', 'bean'],
    broaderNativeRange: ['Mexico', 'Central America', 'Andean South America'],
    origin: 'Common bean is an American domesticate with major domestication lineages in Mesoamerica and the Andes.',
    nativeHabitat: 'Wild bean relatives are twining plants of warm thickets, woodland edges, scrub, and disturbed seasonal habitats.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Pisum sativum',
    commonNames: ['peas', 'pea', 'garden pea'],
    broaderNativeRange: ['Eastern Mediterranean', 'Western Asia'],
    origin: 'Garden pea is an Old World domesticate associated with eastern Mediterranean and western Asian wild ancestry.',
    nativeHabitat: 'Wild relatives grow in cool-season open ground, scrub, field margins, and rocky slopes.',
    currentDistribution: 'Widely cultivated in cool-season growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Brassica oleracea var. botrytis',
    commonNames: ['cauliflower'],
    broaderNativeRange: ['Western Europe', 'Mediterranean Europe'],
    origin: 'Cauliflower is a cultivated form of Brassica oleracea, whose wild coastal ancestors are native to western and Mediterranean Europe.',
    nativeHabitat: 'Wild Brassica oleracea grows on maritime cliffs, rocky coastal soils, and open calcareous seaside habitats.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'cultivar-group',
  }),
  broadNativeRangeRecord({
    scientificName: 'Brassica oleracea var. gemmifera',
    commonNames: ['brussels sprouts', 'brussels sprout'],
    broaderNativeRange: ['Western Europe', 'Mediterranean Europe'],
    origin: 'Brussels sprouts are a cultivated form of Brassica oleracea, a species native in wild coastal forms to western and Mediterranean Europe.',
    nativeHabitat: 'Wild Brassica oleracea occupies maritime cliffs, coastal grassland, rocky calcareous slopes, and salt-exposed open ground.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'cultivar-group',
  }),
  broadNativeRangeRecord({
    scientificName: 'Solanum melongena',
    commonNames: ['eggplant', 'aubergine'],
    broaderNativeRange: ['South Asia', 'Southeast Asia'],
    origin: 'Eggplant is an Old World domesticate derived from Solanum lineages native to South and Southeast Asia.',
    nativeHabitat: 'Wild relatives occur in warm scrub, woodland margins, disturbed ground, and tropical to subtropical open habitats.',
    currentDistribution: 'Widely cultivated in warm growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Apium graveolens',
    commonNames: ['celery'],
    broaderNativeRange: ['Europe', 'North Africa', 'Western Asia'],
    origin: 'Celery is derived from wild Apium graveolens, native around Europe, North Africa, and western Asia.',
    nativeHabitat: 'Wet coastal ground, marshes, ditches, brackish margins, stream edges, and damp disturbed soils.',
    currentDistribution: 'Naturalized across many temperate regions, especially in damp disturbed habitats.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Asparagus officinalis',
    commonNames: ['asparagus'],
    broaderNativeRange: ['Europe', 'Western Asia', 'North Africa'],
    origin: 'Asparagus is native to Europe, western Asia, and North Africa.',
    nativeHabitat: 'Coastal dunes, dry grasslands, scrub, river terraces, sandy soils, and open well-drained habitats.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Brassica rapa var. rapa',
    commonNames: ['turnip'],
    broaderNativeRange: ['Europe', 'Western Asia'],
    origin: 'Turnip is a cultivated root form of Brassica rapa, a species native across parts of Europe and western Asia.',
    nativeHabitat: 'Open disturbed ground, field edges, river margins, roadsides, and seasonally cool habitats.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
    distributionComplexity: 'cultivar-group',
  }),
  broadNativeRangeRecord({
    scientificName: 'Abelmoschus esculentus',
    commonNames: ['okra'],
    broaderNativeRange: ['Africa'],
    origin: 'Okra is an Old World domesticate with African ancestry, though the exact wild origin is debated.',
    nativeHabitat: 'Wild relatives occur in warm open ground, savanna margins, disturbed soils, and tropical seasonal habitats.',
    currentDistribution: 'Widely cultivated in warm climates; not native outside its region of origin.',
    distributionComplexity: 'uncertain',
  }),
  broadNativeRangeRecord({
    scientificName: 'Allium ampeloprasum',
    commonNames: ['leek'],
    broaderNativeRange: ['Mediterranean Basin', 'Western Asia'],
    origin: 'Leek is a cultivated form associated with wild Allium ampeloprasum, native around the Mediterranean and western Asia.',
    nativeHabitat: 'Rocky open ground, field margins, dry slopes, coastal scrub, and disturbed Mediterranean soils.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'cultivar-group',
  }),
  broadNativeRangeRecord({
    scientificName: 'Eruca vesicaria',
    commonNames: ['arugula', 'rocket'],
    broaderNativeRange: ['Mediterranean Basin', 'Western Asia'],
    origin: 'Arugula is native to the Mediterranean region and western Asia.',
    nativeHabitat: 'Dry open ground, field margins, disturbed soils, roadsides, and seasonally cool Mediterranean habitats.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Mentha spicata',
    commonNames: ['mint', 'spearmint'],
    broaderNativeRange: ['Europe', 'Western Asia'],
    origin: 'Spearmint is native to Europe and western Asia, with a long history of cultivation and naturalization beyond that range.',
    nativeHabitat: 'Damp meadows, stream margins, ditches, wet grasslands, and open moist soils.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Salvia rosmarinus',
    commonNames: ['rosemary'],
    broaderNativeRange: ['Mediterranean Basin'],
    origin: 'Rosemary is native to the Mediterranean region, especially dry coastal and limestone habitats.',
    nativeHabitat: 'Rocky slopes, coastal scrub, garrigue, maquis, limestone banks, and dry open Mediterranean shrublands.',
    currentDistribution: 'Widely cultivated in mild climates; not native outside its region of origin.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Lavandula angustifolia',
    commonNames: ['lavender', 'english lavender'],
    broaderNativeRange: ['Western Mediterranean', 'Southern Europe'],
    origin: 'English lavender is native to parts of the western Mediterranean and southern Europe.',
    nativeHabitat: 'Dry rocky limestone slopes, open scrub, mountain meadows, and sunny well-drained Mediterranean habitats.',
    currentDistribution: 'Widely cultivated in dry, mild climates; not native outside its region of origin.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Thymus vulgaris',
    commonNames: ['thyme', 'common thyme'],
    broaderNativeRange: ['Western Mediterranean', 'Southern Europe'],
    origin: 'Common thyme is native to the western Mediterranean region and southern Europe.',
    nativeHabitat: 'Dry rocky slopes, limestone scrub, garrigue, open banks, and thin well-drained soils.',
    currentDistribution: 'Widely cultivated in dry, mild climates; not native outside its region of origin.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Origanum vulgare',
    commonNames: ['oregano'],
    broaderNativeRange: ['Europe', 'North Africa', 'Temperate Asia'],
    origin: 'Oregano is native across much of Europe, North Africa, and temperate Asia.',
    nativeHabitat: 'Dry grasslands, sunny banks, rocky slopes, open woods, and well-drained calcareous soils.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Coriandrum sativum',
    commonNames: ['cilantro', 'coriander'],
    broaderNativeRange: ['Mediterranean Basin', 'Western Asia'],
    origin: 'Coriander is an Old World annual herb associated with Mediterranean and western Asian native and early cultivated ranges.',
    nativeHabitat: 'Open disturbed ground, field margins, dry slopes, and seasonally cool Mediterranean habitats.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Anethum graveolens',
    commonNames: ['dill'],
    broaderNativeRange: ['Mediterranean Basin', 'Western Asia', 'Central Asia'],
    origin: 'Dill is native to the Mediterranean region and western to central Asia.',
    nativeHabitat: 'Sunny disturbed soils, field margins, dry open ground, and seasonally cool grasslands.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Petroselinum crispum',
    commonNames: ['parsley'],
    broaderNativeRange: ['Central Mediterranean', 'Eastern Mediterranean'],
    origin: 'Parsley is native to the Mediterranean region, especially rocky and coastal habitats of southern Europe and nearby western Asia.',
    nativeHabitat: 'Rocky slopes, walls, coastal ground, disturbed soils, and seasonally moist Mediterranean sites.',
    currentDistribution: 'Widely cultivated in suitable growing regions worldwide; not native outside its region of origin.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Salvia officinalis',
    commonNames: ['sage', 'common sage'],
    broaderNativeRange: ['Balkan Peninsula', 'Central Mediterranean'],
    origin: 'Common sage is native to the central Mediterranean and Balkan region.',
    nativeHabitat: 'Dry limestone slopes, rocky scrub, maquis, garrigue, and sunny well-drained Mediterranean habitats.',
    currentDistribution: 'Widely cultivated in dry, mild climates; not native outside its region of origin.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Foeniculum vulgare',
    commonNames: ['fennel'],
    broaderNativeRange: ['Mediterranean Basin'],
    origin: 'Fennel is native to the Mediterranean region.',
    nativeHabitat: 'Dry open slopes, roadsides, field margins, coastal ground, and disturbed Mediterranean soils.',
    currentDistribution: 'Naturalized across many mild and temperate regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Symphytum officinale',
    commonNames: ['comfrey', 'common comfrey'],
    broaderNativeRange: ['Europe', 'Western Asia'],
    origin: 'Common comfrey is native to Europe and western Asia.',
    nativeHabitat: 'Damp meadows, stream banks, wet ditches, woodland edges, and moist nitrogen-rich soils.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Cymbopogon citratus',
    commonNames: ['lemongrass'],
    broaderNativeRange: ['South Asia', 'Southeast Asia'],
    origin: 'Lemongrass is a tropical Old World grass associated with South and Southeast Asian origin, though cultivated lineages obscure the exact wild range.',
    nativeHabitat: 'Warm grasslands, open slopes, disturbed tropical ground, and seasonally wet-to-dry margins.',
    currentDistribution: 'Widely cultivated in warm climates; not native outside its region of origin.',
    distributionComplexity: 'uncertain',
  }),
  broadNativeRangeRecord({
    scientificName: 'Fragaria x ananassa',
    commonNames: ['strawberry', 'garden strawberry'],
    broaderNativeRange: ['Cultivated hybrid of North and South American Fragaria lineages'],
    origin: 'Garden strawberry is a cultivated hybrid derived principally from North American Fragaria virginiana and South American/western American Fragaria chiloensis.',
    nativeHabitat: 'The parent species occupy meadows, dunes, open woods, coastal bluffs, and cool grasslands, but the garden hybrid itself is cultivated.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'hybrid',
  }),
  broadNativeRangeRecord({
    scientificName: 'Rubus fruticosus',
    commonNames: ['blackberry'],
    broaderNativeRange: ['Europe', 'Western Asia', 'North Africa'],
    origin: 'Rubus fruticosus is commonly treated as a complex or aggregate of blackberry lineages native mainly to Europe, western Asia, and North Africa.',
    nativeHabitat: 'Hedgerows, woodland edges, thickets, disturbed ground, roadsides, and sunny margins.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
    distributionComplexity: 'aggregate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Ficus carica',
    commonNames: ['fig', 'common fig'],
    broaderNativeRange: ['Mediterranean Basin', 'Western Asia'],
    origin: 'Common fig is native to the Mediterranean region and western Asia.',
    nativeHabitat: 'Rocky slopes, cliffs, stream ravines, dry open woodland, and limestone crevices in warm climates.',
    currentDistribution: 'Naturalized across many mild regions, especially near settlement and disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Vitis vinifera',
    commonNames: ['grape', 'wine grape'],
    broaderNativeRange: ['Mediterranean Basin', 'Western Asia', 'Caucasus'],
    origin: 'Vitis vinifera is native from the Mediterranean region through western Asia and the Caucasus.',
    nativeHabitat: 'Riparian woodland, forest edges, rocky slopes, scrub, and climbing woodland margins.',
    currentDistribution: 'Naturalized across many mild and temperate regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Malus domestica',
    commonNames: ['apple'],
    broaderNativeRange: ['Central Asia'],
    origin: 'Domestic apple is a cultivated complex strongly associated with Central Asian Malus sieversii ancestry.',
    nativeHabitat: 'Wild apple relatives grow in montane forests, woodland edges, and foothill habitats of Central Asia.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'cultivar-group',
  }),
  broadNativeRangeRecord({
    scientificName: 'Prunus persica',
    commonNames: ['peach'],
    broaderNativeRange: ['China'],
    origin: 'Peach is native to China, where wild and early cultivated lineages occur in northern and northwestern regions.',
    nativeHabitat: 'Rocky slopes, dry valleys, woodland margins, and foothill habitats.',
    currentDistribution: 'Widely cultivated in temperate fruit-growing regions; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Prunus avium',
    commonNames: ['cherry', 'sweet cherry'],
    broaderNativeRange: ['Europe', 'Western Asia'],
    origin: 'Sweet cherry is native to Europe and western Asia.',
    nativeHabitat: 'Open woodlands, forest edges, hedgerows, slopes, and well-drained temperate soils.',
    currentDistribution: 'Naturalized across parts of temperate Europe and western Asia; also cultivated beyond its native range.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Pyrus communis',
    commonNames: ['pear', 'european pear'],
    broaderNativeRange: ['Eastern Europe', 'Western Asia', 'Caucasus'],
    origin: 'European pear is a cultivated complex associated with wild Pyrus lineages of eastern Europe, western Asia, and the Caucasus.',
    nativeHabitat: 'Open woods, scrub, woodland margins, rocky slopes, and foothill habitats.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'cultivar-group',
  }),
  broadNativeRangeRecord({
    scientificName: 'Punica granatum',
    commonNames: ['pomegranate'],
    broaderNativeRange: ['Iran', 'Western Asia', 'Himalayan region'],
    origin: 'Pomegranate is native to western Asia, with long cultivation centered around Iran and adjacent regions.',
    nativeHabitat: 'Dry rocky slopes, scrub, open woodland, and warm semi-arid valleys.',
    currentDistribution: 'Naturalized across many warm regions, especially near settlement and disturbed habitats.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Citrullus lanatus',
    commonNames: ['watermelon'],
    broaderNativeRange: ['Northeastern Africa', 'Southern Africa'],
    origin: 'Watermelon is an African domesticate derived from Citrullus lineages native to Africa.',
    nativeHabitat: 'Dry sandy ground, savanna margins, seasonally arid riverbeds, and warm open disturbed soils.',
    currentDistribution: 'Widely cultivated in warm climates; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Cucumis melo',
    commonNames: ['cantaloupe', 'melon'],
    broaderNativeRange: ['Africa', 'Southwestern Asia', 'South Asia'],
    origin: 'Melon is an Old World domesticate with native and early crop ancestry across Africa and parts of Asia.',
    nativeHabitat: 'Warm dry open ground, scrub, field margins, disturbed soils, and seasonally arid habitats.',
    currentDistribution: 'Widely cultivated in warm climates; not native outside its region of origin.',
    distributionComplexity: 'domesticate',
  }),
  broadNativeRangeRecord({
    scientificName: 'Citrus limon',
    commonNames: ['lemon'],
    broaderNativeRange: ['Cultivated hybrid of Asian Citrus lineages'],
    origin: 'Lemon is a cultivated Citrus hybrid with ancestry in Asian citrus lineages rather than a simple wild species range.',
    nativeHabitat: 'Parent citrus lineages occupy warm subtropical forests, forest edges, and monsoon-influenced habitats.',
    currentDistribution: 'Cultivated globally; does not occur as a native plant in this form.',
    distributionComplexity: 'hybrid',
  }),
  broadNativeRangeRecord({
    scientificName: 'Passiflora edulis',
    commonNames: ['passion fruit', 'passionfruit'],
    broaderNativeRange: ['South America'],
    origin: 'Passion fruit is native to South America, especially tropical and subtropical regions of the continent.',
    nativeHabitat: 'Forest edges, thickets, disturbed tropical ground, woodland margins, and climbing vegetation in warm humid climates.',
    currentDistribution: 'Naturalized across many tropical and subtropical regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Tagetes patula',
    commonNames: ['marigold', 'french marigold'],
    broaderNativeRange: ['Mexico', 'Central America'],
    origin: 'French marigold is native to Mexico and Central America.',
    nativeHabitat: 'Open sunny slopes, disturbed ground, field margins, and warm seasonal habitats.',
    currentDistribution: 'Widely cultivated in gardens worldwide; not native outside its region of origin.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Zinnia elegans',
    commonNames: ['zinnia'],
    broaderNativeRange: ['Mexico'],
    origin: 'Common garden zinnia is native to Mexico.',
    nativeHabitat: 'Dry open grasslands, scrub, rocky slopes, and sunny disturbed ground.',
    currentDistribution: 'Widely cultivated in gardens worldwide; not native outside its region of origin.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Tropaeolum majus',
    commonNames: ['nasturtium'],
    broaderNativeRange: ['Andean South America'],
    origin: 'Garden nasturtium is native to Andean South America.',
    nativeHabitat: 'Open slopes, rocky ground, disturbed soils, and cool tropical mountain margins.',
    currentDistribution: 'Naturalized across some mild climates, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Calendula officinalis',
    commonNames: ['calendula', 'pot marigold'],
    broaderNativeRange: ['Mediterranean Basin'],
    origin: 'Calendula is associated with the Mediterranean region, though long cultivation obscures parts of its original wild distribution.',
    nativeHabitat: 'Open sunny ground, field edges, disturbed soils, and seasonally moist Mediterranean habitats.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
    distributionComplexity: 'uncertain',
  }),
  broadNativeRangeRecord({
    scientificName: 'Borago officinalis',
    commonNames: ['borage'],
    broaderNativeRange: ['Mediterranean Basin'],
    origin: 'Borage is native to the Mediterranean region.',
    nativeHabitat: 'Open disturbed ground, field margins, rocky slopes, roadsides, and dry Mediterranean soils.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Matricaria chamomilla',
    commonNames: ['chamomile', 'german chamomile'],
    broaderNativeRange: ['Europe', 'Western Asia'],
    origin: 'German chamomile is native to Europe and western Asia.',
    nativeHabitat: 'Open fields, disturbed soils, roadsides, cereal-field margins, and seasonally cool ground.',
    currentDistribution: 'Naturalized across many temperate regions, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Lobularia maritima',
    commonNames: ['sweet alyssum'],
    broaderNativeRange: ['Mediterranean Basin', 'Macaronesia'],
    origin: 'Sweet alyssum is native to the Mediterranean region and Macaronesia.',
    nativeHabitat: 'Coastal cliffs, rocky slopes, dry open ground, and well-drained maritime habitats.',
    currentDistribution: 'Naturalized across many mild climates, especially in disturbed habitats.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Cosmos bipinnatus',
    commonNames: ['cosmos'],
    broaderNativeRange: ['Mexico'],
    origin: 'Garden cosmos is native to Mexico.',
    nativeHabitat: 'Open meadows, grasslands, field edges, roadsides, and sunny disturbed soils.',
    currentDistribution: 'Widely cultivated in gardens worldwide; not native outside its region of origin.',
  }),
  broadNativeRangeRecord({
    scientificName: 'Ipomoea purpurea',
    commonNames: ['morning glory', 'common morning glory'],
    broaderNativeRange: ['Mexico', 'Central America'],
    origin: 'Common morning glory is native to Mexico and Central America.',
    nativeHabitat: 'Warm open ground, thickets, field margins, roadsides, and disturbed soils where vines can climb surrounding vegetation.',
    currentDistribution: 'Naturalized across many warm and temperate regions, especially in disturbed habitats.',
  }),
  {
    scientificName: 'Allium schoenoprasum',
    commonNames: ['chives'],
    source: 'POWO; Native Plant Trust',
    sourceNotes: [
      'Imported from reviewed Kew Plants of the World Online distribution records and Native Plant Trust state-level notes; mixed Newfoundland/Labrador province-level status remains uncertain.',
    ],
    sourceReliability: 'authoritative',
    coverageLevel: 'partial',
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'uncertain', note: 'Labrador and Newfoundland differ in source status; province-level matching is kept out of native results.' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-NT', status: 'native' },
      { code: 'CA-NU', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'CA-YT', status: 'native' },
      { code: 'US-AK', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'introduced' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'introduced' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['Temperate Northern Hemisphere', 'Northern North America', 'Eurasia'],
    origin: 'Chives are a circumboreal Allium native across cool regions of the Northern Hemisphere, including documented native populations in northern and montane North America as well as Eurasia.',
    nativeHabitat: 'Moist meadows, river and lake shores, floodplains, damp ledges, cool grasslands, and open rocky ground.',
    currentDistribution: 'Occurs naturally across cool regions of the Northern Hemisphere; also cultivated beyond its native range.',
  },
  {
    scientificName: 'Artemisia dracunculus',
    commonNames: ['tarragon', 'french tarragon'],
    source: 'USDA FEIS; POWO',
    sourceNotes: [
      'Imported from USDA Forest Service FEIS and Kew Plants of the World Online records for wild Artemisia dracunculus; nursery French tarragon cultivars are not assumed to be local ecotypes.',
    ],
    sourceReliability: 'authoritative',
    coverageLevel: 'partial',
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'CA-YT', status: 'native' },
      { code: 'US-AK', status: 'uncertain', note: 'Authoritative sources disagree on Alaska status.' },
      { code: 'US-AZ', status: 'native' },
      { code: 'US-CA', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' },
      { code: 'US-NV', status: 'native' },
      { code: 'US-OR', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-UT', status: 'native' },
      { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['Western and central North America', 'Temperate Northern Hemisphere'],
    origin: 'Wild Artemisia dracunculus is native across parts of western and central North America and Eurasia.',
    nativeHabitat: 'Open dry to mesic grasslands, prairie edges, sagebrush steppe, riparian terraces, floodplains, rocky slopes, and well-drained open ground.',
    currentDistribution: 'Occurs naturally across western and central North America and Eurasia; also cultivated beyond its native range.',
  },
  {
    scientificName: 'Echinacea purpurea',
    commonNames: ['echinacea', 'purple coneflower'],
    source: 'POWO',
    sourceNotes: ['Imported from Kew Plants of the World Online exact native and introduced distribution records for Echinacea purpurea.'],
    sourceReliability: 'authoritative',
    coverageLevel: 'partial',
    regions: [
      { code: 'CA-ON', status: 'introduced' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-DE', status: 'introduced' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MD', status: 'introduced' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WI', status: 'native' },
    ],
    broaderNativeRange: ['Central and eastern North America'],
    origin: 'Purple coneflower is native to central and eastern North America, especially prairie, savanna, and open woodland regions of the United States.',
    nativeHabitat: 'Open prairies, glades, dry woods, savannas, and well-drained meadow edges.',
    currentDistribution: 'Occurs naturally across central and eastern North America; also cultivated beyond its native range.',
  },
  {
    scientificName: 'Rudbeckia hirta',
    commonNames: ['black-eyed susan', 'black eyed susan'],
    source: 'USDA FEIS; POWO',
    sourceNotes: ['Imported from USDA Forest Service FEIS and Kew Plants of the World Online exact distribution records for Rudbeckia hirta.'],
    sourceReliability: 'authoritative',
    coverageLevel: 'partial',
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-AK', status: 'introduced' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CA', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Central North America'],
    origin: 'Black-eyed Susan is native across much of eastern and central North America.',
    nativeHabitat: 'Sunny prairies, meadows, open woods, roadsides, disturbed fields, and dry slopes.',
    currentDistribution: 'Occurs naturally across eastern and central North America; also cultivated beyond its native range.',
  },
  {
    scientificName: 'Monarda didyma',
    commonNames: ['bee balm', 'scarlet bee balm'],
    source: 'POWO; Native Plant Trust',
    sourceNotes: [
      'Imported from Kew Plants of the World Online exact native and introduced distribution records, with Native Plant Trust notes keeping Rhode Island unconfirmed.',
    ],
    sourceReliability: 'authoritative',
    coverageLevel: 'partial',
    regions: [
      { code: 'CA-ON', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IL', status: 'introduced' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'introduced' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'uncertain' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'introduced' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Bee balm is native to eastern North America, especially moist northeastern, Appalachian, and Great Lakes woodland-edge regions.',
    nativeHabitat: 'Moist woods, stream banks, rich woodland edges, wet meadows, and rocky slopes.',
    currentDistribution: 'Occurs naturally across eastern North America; also cultivated beyond its native range.',
  },
  broadNativeRangeRecord({
    scientificName: 'Trifolium repens',
    commonNames: ['white clover', 'dutch clover', 'clover'],
    broaderNativeRange: ['Europe', 'Western Asia', 'Central Asia'],
    origin: 'White clover is native to Europe and western to central Asia, with a natural range spanning temperate grasslands from the British Isles and Mediterranean to the Caucasus and Central Asian steppes.',
    nativeHabitat: 'Meadows, pastures, lawns, roadsides, and open grasslands in temperate regions with moderate moisture and reasonable soil fertility.',
    currentDistribution: 'Naturalized across temperate regions worldwide, including North America, Australia, and New Zealand, primarily through introduction as a pasture and cover crop.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Carya illinoinensis',
    commonNames: ['pecan'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; native to alluvial bottomlands of the Mississippi River basin and south-central river systems.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
    ],
    broaderNativeRange: ['South-central North America', 'Mississippi River basin'],
    origin: 'Pecan is native to the alluvial bottomland forests of the Mississippi River drainage and south-central river systems, from Iowa and Illinois south through Texas and into northern Mexico.',
    nativeHabitat: 'Rich, moist alluvial bottomland forests, river floodplains, and mesic upland woodlands along major river systems of the south-central United States.',
    currentDistribution: 'Widely cultivated across the southeastern and south-central United States and planted well beyond its native range; also grown commercially in Mexico, South Africa, and Australia.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Castanea dentata',
    commonNames: ['american chestnut', 'chestnut'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; functionally extinct as a canopy tree due to chestnut blight but native root systems persist throughout original range.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Appalachian Mountains'],
    origin: 'Native to eastern North America, from southern Maine south along the Appalachians to Alabama, and west through the Ohio Valley to lower Michigan and Wisconsin.',
    nativeHabitat: 'Upland deciduous forests on well-drained, acidic, often rocky or sandy slopes and ridges; a dominant canopy species of mixed mesophytic and chestnut-oak forest communities of the Appalachians and piedmont.',
    currentDistribution: 'Functionally absent from the forest canopy due to chestnut blight; persists as root-sprout shrubs throughout original range. Blight-resistant hybrids and Chinese chestnut (Castanea mollissima) cultivated throughout eastern North America.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Acer saccharum',
    commonNames: ['sugar maple', 'hard maple', 'rock maple'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; primary commercial maple syrup species.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Great Lakes region'],
    origin: 'Native to eastern North America, from Newfoundland and Nova Scotia west to Manitoba, south to Georgia, Tennessee, and Kansas.',
    nativeHabitat: 'Mixed hardwood forests on rich, moist, well-drained upland slopes and valley floors; a dominant canopy tree in the northern hardwood forest community, often with beech, yellow birch, and basswood.',
    currentDistribution: 'Abundant throughout original range; cultivated widely as a street and shade tree. Commercial maple syrup production concentrated in Quebec, Vermont, New York, and Michigan.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Betula papyrifera',
    commonNames: ['paper birch', 'white birch', 'canoe birch'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; one of the most widely distributed trees in North America.',
    ],
    regions: [
      { code: 'US-AK', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-NT', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'CA-YT', status: 'native' },
    ],
    broaderNativeRange: ['Northern North America', 'Boreal Forest', 'Rocky Mountains'],
    origin: 'Native to most of Canada and the northern United States, from Alaska to Newfoundland, south into the Great Lakes, northern Appalachians, and intermountain West.',
    nativeHabitat: 'Pioneer species in burned or disturbed boreal forest, lake shores, stream banks, and cool upland woods; often forms pure stands after disturbance before being replaced by conifers or maples.',
    currentDistribution: 'Abundant throughout the boreal zone; widely cultivated as an ornamental for striking white bark. Heritage varieties planted across temperate regions worldwide.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Carya ovata',
    commonNames: ['shagbark hickory', 'shellbark hickory'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; named for its distinctive shaggy exfoliating bark.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America, from southern Quebec and Ontario south through the eastern and central United States to Texas and Florida.',
    nativeHabitat: 'Rich upland deciduous forests, slopes, and valleys; often on well-drained, deep loamy soils; common associate of oak, hickory, and maple in mixed hardwood communities.',
    currentDistribution: 'Abundant throughout original range; cultivated for its sweet, flavorful nuts and durable wood. One of the most valuable native nut trees in eastern North America.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Tilia americana',
    commonNames: ['american linden', 'basswood', 'american basswood'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; important nectar source and traditional medicinal plant.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Great Plains'],
    origin: 'Native to eastern and central North America, from New Brunswick and Manitoba south through the eastern United States to northern Georgia, Alabama, and Oklahoma.',
    nativeHabitat: 'Rich mesic deciduous forests, ravines, and stream banks; often on moist, deep, well-drained soils; a characteristic associate in northern hardwood and mixed mesophytic forest communities.',
    currentDistribution: 'Common throughout original range; widely planted as a street and shade tree. Flowers harvested for linden (tilleul) herbal tea with longstanding use in European and North American herbalism.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Catalpa speciosa',
    commonNames: ['northern catalpa', 'hardy catalpa', 'cigar tree'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; narrow native range in the central Mississippi Valley; widely naturalized elsewhere.',
    ],
    regions: [
      { code: 'US-AR', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-TN', status: 'native' },
    ],
    broaderNativeRange: ['Central Mississippi Valley', 'Lower Ohio Valley'],
    origin: 'Native to a relatively small area of the central United States, primarily the lower Ohio Valley and central Mississippi River Valley.',
    nativeHabitat: 'Bottomland forests, floodplains, and stream banks on rich, moist alluvial soils; grows in mixed hardwood forest alongside cottonwood, sycamore, and silver maple.',
    currentDistribution: 'Widely planted far beyond its native range as a street and park tree across the United States and southern Canada; naturalized in many eastern states.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Aesculus glabra',
    commonNames: ['ohio buckeye', 'fetid buckeye', 'stinking buckeye'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; state tree of Ohio; all parts are toxic to humans and livestock.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Central Eastern North America', 'Ohio Valley', 'Great Plains margins'],
    origin: 'Native to the central and eastern United States, from western Pennsylvania and Alabama west through the Ohio Valley to Nebraska, Kansas, Oklahoma, and Texas.',
    nativeHabitat: 'Rich bottomlands, moist upland slopes, and stream banks in deciduous mixed hardwood forests; often on fertile, well-drained to moist soils.',
    currentDistribution: 'Common throughout original range; state tree of Ohio. Cultivated as an ornamental despite its toxicity - all parts are poisonous to humans and most livestock.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Magnolia grandiflora',
    commonNames: ['southern magnolia', 'bull bay', 'evergreen magnolia'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; state tree and flower of Mississippi and Louisiana.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
    ],
    broaderNativeRange: ['Southeastern United States', 'Gulf Coastal Plain'],
    origin: 'Native to the coastal plains and lower piedmont of the southeastern United States, from eastern Virginia south to central Florida and west to eastern Texas.',
    nativeHabitat: 'Moist, fertile lowland forests, stream banks, and hammocks; in mixed hardwood-pine forests on rich, acidic, well-drained to seasonally wet soils.',
    currentDistribution: 'Abundant in its native Southeast; widely cultivated throughout the world in USDA zones 7-10 as one of the most iconic ornamental trees. State tree and flower of Mississippi and Louisiana.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Liriodendron tulipifera',
    commonNames: ['tulip poplar', 'tulip tree', 'yellow poplar'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; state tree of Indiana, Kentucky, and Tennessee; tallest native hardwood in eastern North America.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Appalachian region'],
    origin: 'Native to eastern North America, from southern Ontario and Vermont south to Florida and west to Michigan, Illinois, Arkansas, and Louisiana.',
    nativeHabitat: 'Rich, moist upland and bottomland deciduous forests; a canopy dominant in mixed mesophytic and cove hardwood communities, particularly in the Appalachians; prefers deep, well-drained, slightly acidic soils.',
    currentDistribution: 'Abundant throughout original range; the tallest native hardwood in eastern North America. Widely planted as a large ornamental and street tree. State tree of Indiana, Kentucky, and Tennessee.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Quercus alba',
    commonNames: ['white oak'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; keystone species supporting more wildlife than almost any other native tree.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America, from southern Maine and Quebec south to northern Florida, and west to Minnesota, Nebraska, Kansas, Oklahoma, and Texas.',
    nativeHabitat: 'Upland and lowland deciduous forests on a wide range of soils; tolerates dry rocky ridges to moist bottomlands; a dominant canopy tree in oak-hickory and oak-pine forest communities throughout its range.',
    currentDistribution: 'Abundant throughout original range; a foundational keystone species supporting hundreds of caterpillar species and providing critical wildlife food and habitat. Widely used in landscape planting.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Quercus rubra',
    commonNames: ['red oak', 'northern red oak'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; one of the most important timber and wildlife trees in eastern North America.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-PE', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America, from Nova Scotia and New Brunswick west to Minnesota, south to Georgia, Alabama, and Oklahoma.',
    nativeHabitat: 'Upland forests, slopes, and ridges on a wide variety of soils; tolerates both dry and moderately moist sites; common associate in oak-hickory and northern hardwood forests.',
    currentDistribution: 'Abundant throughout original range; one of the most widely planted native oaks for timber, wildlife, and shade. Fast-growing for an oak; widely used in urban and suburban landscapes.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Juglans nigra',
    commonNames: ['black walnut', 'eastern black walnut'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; produces juglone, which is allelopathic to many plants.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Great Plains margins'],
    origin: 'Native to eastern North America, from Massachusetts and southern Ontario west to South Dakota, south to Florida and Texas.',
    nativeHabitat: 'Rich, moist upland forests, stream banks, and bottomlands on deep, well-drained to moderately moist soils; often on limestone-derived or fertile alluvial soils.',
    currentDistribution: 'Common throughout original range; widely valued for its hard, richly grained timber and intensely flavored nuts. Produces juglone, a compound that suppresses many plants in its root zone.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Acer rubrum',
    commonNames: ['red maple', 'swamp maple', 'soft maple'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; one of the most abundant and widespread trees in eastern North America.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America, from Newfoundland west to Manitoba, south to Florida and Texas; one of the most geographically widespread trees on the continent.',
    nativeHabitat: 'Extraordinarily adaptable; occurs in swamps, bogs, upland forests, stream banks, and dry ridges; tolerates both waterlogged and dry soils better than almost any other eastern hardwood.',
    currentDistribution: 'Extremely abundant throughout original range; the most common tree in eastern North America by some estimates. Widely planted for spectacular red fall color; state tree of Rhode Island.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Cercis canadensis',
    commonNames: ['eastern redbud', 'redbud'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; one of the most celebrated spring-flowering native trees.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Central Plains'],
    origin: 'Native to eastern and central North America, from New Jersey south to northern Florida, and west through the interior to Nebraska, Kansas, Oklahoma, and Texas; also extends into Ontario.',
    nativeHabitat: 'Understory and forest edges in mesic to well-drained deciduous forests; commonly found on slopes, bluffs, and stream banks in oak-hickory and mixed hardwood communities.',
    currentDistribution: 'Common throughout original range; among the most widely planted native flowering trees in North America, valued for brilliant magenta-pink flowers that emerge directly from bark before leaves in early spring.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Amelanchier canadensis',
    commonNames: ['serviceberry', 'shadblow', 'juneberry', 'shadbush'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'State-level native distribution from USDA PLANTS Database; multiple Amelanchier species overlap in range; this record covers the eastern serviceberry complex.',
    ],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America, from Newfoundland and Ontario south to Georgia and Alabama, primarily east of the Mississippi.',
    nativeHabitat: 'Forest edges, woodland margins, shrubby thickets, stream banks, and swamp edges; tolerates both moist and dry sites; common in early successional habitats and as an understory species.',
    currentDistribution: 'Common throughout original range; widely cultivated as a multi-season ornamental for white spring flowers, edible blueberry-like fruit in June, and brilliant fall color.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Schizachyrium scoparium',
    commonNames: ['little bluestem', 'beard grass'],
    source: 'USDA PLANTS',
    sourceNotes: ['State-level native distribution from USDA PLANTS Database; one of the most widespread native grasses in North America.'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NM', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' }, { code: 'US-WY', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and Central North America', 'Great Plains', 'Rocky Mountain foothills'],
    origin: 'Native to grasslands across central and eastern North America, from the Atlantic coast west to the Rocky Mountain foothills and from southern Canada south to Mexico.',
    nativeHabitat: 'Shortgrass, mixed-grass, and tallgrass prairies; open woodlands and savannas; rocky glades and dry roadsides on well-drained soils.',
    currentDistribution: 'Common in remnant prairies; widely cultivated as an ornamental grass throughout North America and Europe for exceptional fall color and winter form.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Panicum virgatum',
    commonNames: ['switchgrass'],
    source: 'USDA PLANTS',
    sourceNotes: ['State-level native distribution from USDA PLANTS Database; one of the dominant tallgrass prairie species.'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NM', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' }, { code: 'US-WY', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and Central North America', 'Atlantic and Gulf coastal marshes'],
    origin: 'Native across most of eastern and central North America, from Nova Scotia and Manitoba south through the Great Plains to Mexico and east to the Atlantic and Gulf coasts.',
    nativeHabitat: 'Tallgrass and mixed-grass prairies, coastal salt marshes, pine savannas, open woodlands, and disturbed sites on a wide range of soils from wet to dry.',
    currentDistribution: 'Abundant in remnant prairies and coastal marshes; one of the most widely planted native ornamental grasses in North America and Europe.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Sporobolus heterolepis',
    commonNames: ['prairie dropseed'],
    source: 'USDA PLANTS',
    sourceNotes: ['State-level native distribution from USDA PLANTS Database; indicator species of high-quality remnant prairie.'],
    regions: [
      { code: 'US-AR', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-ND', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WY', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Central and Eastern North America', 'Tallgrass Prairie', 'Prairie Peninsula'],
    origin: 'Native to the tallgrass and mixed-grass prairie regions of central and eastern North America, most abundant in the prairie peninsula extending east through Illinois and Ohio.',
    nativeHabitat: 'Dry to mesic upland prairies on well-drained soils; a strong indicator of high-quality remnant prairie; does not colonise disturbed ground readily.',
    currentDistribution: 'Uncommon to locally abundant in remnant prairies; widely grown in native plant nurseries and naturalistic garden design.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Sorghastrum nutans',
    commonNames: ['indian grass', 'yellow indiangrass'],
    source: 'USDA PLANTS',
    sourceNotes: ['State-level native distribution from USDA PLANTS Database; co-dominant species of the tallgrass prairie.'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CO', status: 'native' }, { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' }, { code: 'US-WY', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-ON', status: 'native' },
    ],
    broaderNativeRange: ['Central and Eastern North America', 'Tallgrass Prairie'],
    origin: 'Native to central and eastern North America, from southern Canada south through the Great Plains and eastern US to the Gulf Coast.',
    nativeHabitat: 'Tallgrass and mixed-grass prairies, open woodlands and savannas; more tolerant of clay and moderate moisture than little bluestem.',
    currentDistribution: 'Common in remnant prairies; widely cultivated in native plant gardens and prairie restorations.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Bouteloua curtipendula',
    commonNames: ['side-oats grama', 'sideoats grama'],
    source: 'USDA PLANTS',
    sourceNotes: ['State-level native distribution from USDA PLANTS Database; state grass of Texas.'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-LA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NM', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-UT', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Central North America', 'Great Plains', 'Eastern US'],
    origin: 'Native to the mixed-grass and shortgrass prairies of central North America, with extensions east to the Atlantic states and south into Mexico and Central America.',
    nativeHabitat: 'Mixed-grass and shortgrass prairies, rocky glades, dry hillsides, and calcareous soils; among the most drought-tolerant native grasses.',
    currentDistribution: 'State grass of Texas; common throughout the Great Plains; widely used in range restoration and xeric native plant landscaping.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Carex pensylvanica',
    commonNames: ['pennsylvania sedge', 'oak sedge'],
    source: 'USDA PLANTS',
    sourceNotes: ['State-level native distribution from USDA PLANTS Database; primary native lawn-alternative sedge for eastern North America.'],
    regions: [
      { code: 'US-CT', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America, from maritime Canada south to Georgia and Alabama, primarily east of the Great Plains.',
    nativeHabitat: 'Dry to mesic oak and mixed hardwood forest understory; rocky and sandy woodland soils; a characteristic groundlayer species of eastern deciduous forest.',
    currentDistribution: 'Common throughout its native range; increasingly grown as a native groundcover and lawn alternative throughout eastern North America.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Deschampsia cespitosa',
    commonNames: ['tufted hairgrass', 'tussock grass'],
    source: 'USDA PLANTS',
    sourceNotes: ['State-level native distribution from USDA PLANTS Database; circumboreal species native across North America, Europe, and Asia.'],
    regions: [
      { code: 'US-AK', status: 'native' }, { code: 'US-AZ', status: 'native' },
      { code: 'US-CA', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-ID', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MT', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NM', status: 'native' },
      { code: 'US-NV', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WY', status: 'native' },
      { code: 'CA-AB', status: 'native' }, { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-NT', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' }, { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' }, { code: 'CA-YT', status: 'native' },
    ],
    broaderNativeRange: ['Circumboreal', 'Arctic and Subarctic North America', 'Western Mountains', 'Northern Europe', 'Asia'],
    origin: 'Circumboreal; native across Arctic, subarctic, and temperate regions of North America, Europe, and Asia.',
    nativeHabitat: 'Wet meadows, stream banks, bogs, mountain snowmelt zones, and moist open woodlands; characteristic of cool, moist habitats.',
    currentDistribution: 'Common in native habitats across the northern hemisphere; widely cultivated as an ornamental grass particularly in European garden design.',
  }),

  // ── NATIVE SHRUBS ──────────────────────────────────────────────────────────

  exactSubdivisionRangeRecord({
    scientificName: 'Ilex verticillata',
    commonNames: ['Winterberry Holly', 'Winterberry', 'Black Alder'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native throughout northeastern and eastern North America in wetland and moist woodland contexts'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' }, { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' }, { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America.',
    nativeHabitat: 'Wetland margins, streambanks, boggy thickets, and moist woodland edges from Newfoundland south to Georgia and west to Minnesota.',
    currentDistribution: 'Widespread across eastern North America; widely cultivated across the continent.',
  }),

  exactSubdivisionRangeRecord({
    scientificName: 'Lindera benzoin',
    commonNames: ['Spicebush', 'Northern Spicebush', 'Benjamin Bush'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to moist forest understory of eastern North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America from Maine to Ontario south to Florida and Texas.',
    nativeHabitat: 'Moist deciduous forest understory, streambanks, and floodplain woodland edges.',
    currentDistribution: 'Widespread in eastern North America; increasingly cultivated in native plant gardens.',
  }),

  exactSubdivisionRangeRecord({
    scientificName: 'Cephalanthus occidentalis',
    commonNames: ['Buttonbush', 'Common Buttonbush', 'Button Willow'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native across a very wide range in wetland habitats of North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' }, { code: 'US-CA', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-OR', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Southwestern United States', 'Central America', 'Caribbean'],
    origin: 'Native to a vast range across North and Central America and the Caribbean.',
    nativeHabitat: 'Swamps, pond margins, streambanks, floodplain forests, and seasonally flooded thickets.',
    currentDistribution: 'Widespread across North and Central America; widely used in rain gardens and wetland restoration.',
  }),

  exactSubdivisionRangeRecord({
    scientificName: 'Viburnum dentatum',
    commonNames: ['Arrowwood Viburnum', 'Southern Arrowwood', 'Arrowwood'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to moist woodland edges and thickets of eastern North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America from New Brunswick south to Georgia and west to the Great Plains.',
    nativeHabitat: 'Moist thickets, woodland edges, streambanks, and forest margins.',
    currentDistribution: 'Widespread across eastern North America; widely cultivated in native plant gardens and restoration plantings.',
  }),

  // ── NATIVE TREES (additional) ──────────────────────────────────────────────

  exactSubdivisionRangeRecord({
    scientificName: 'Prunus serotina',
    commonNames: ['Black Cherry', 'Wild Black Cherry', 'Rum Cherry'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native across an enormous range from eastern Canada south to Guatemala'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' }, { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Central America'],
    origin: 'Native to North America from Nova Scotia and Manitoba south to Guatemala.',
    nativeHabitat: 'Forest edges, disturbed areas, successional woodlands, and roadsides across a vast range.',
    currentDistribution: 'Widespread across most of North America east of the Rockies; introduced and invasive in parts of Western Europe.',
  }),

  exactSubdivisionRangeRecord({
    scientificName: 'Celtis occidentalis',
    commonNames: ['Hackberry', 'Common Hackberry', 'Northern Hackberry'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native across central and eastern North America in a wide range of habitats'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CO', status: 'native' }, { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-MT', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Central and Eastern North America'],
    origin: 'Native to central and eastern North America from Quebec and Manitoba south to North Carolina and Kansas.',
    nativeHabitat: 'River bottoms, rocky bluffs, disturbed woodland edges, and forest margins across a wide range.',
    currentDistribution: 'Widespread in central and eastern North America; widely planted as a street and urban tree.',
  }),

  exactSubdivisionRangeRecord({
    scientificName: 'Nyssa sylvatica',
    commonNames: ['Black Gum', 'Black Tupelo', 'Sour Gum', 'Pepperidge'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to eastern North America in moist forest habitats'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America from southern Maine and Ontario south to Florida and west to Texas.',
    nativeHabitat: 'Moist forest understories, swamp margins, river bottoms, and upland slopes.',
    currentDistribution: 'Widespread across eastern North America; increasingly used as a landscape tree for fall color and wildlife value.',
  }),

  // ── NATIVE WILDFLOWERS (additional) ────────────────────────────────────────

  exactSubdivisionRangeRecord({
    scientificName: 'Zizia aurea',
    commonNames: ['Golden Alexander', 'Golden Alexanders'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to moist prairies and woodland edges of central and eastern North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Central and Eastern North America'],
    origin: 'Native to central and eastern North America from Quebec and Saskatchewan south to Florida and Texas.',
    nativeHabitat: 'Moist prairies, open woodland edges, meadows, and streambanks.',
    currentDistribution: 'Widespread across central and eastern North America within its native range.',
  }),

  exactSubdivisionRangeRecord({
    scientificName: 'Rudbeckia fulgida',
    commonNames: ['Orange Coneflower', 'Showy Coneflower', 'Orange Black-eyed Susan'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to the eastern United States in open woodlands and moist meadows'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern United States'],
    origin: 'Native to the eastern United States.',
    nativeHabitat: 'Open woodlands, moist meadows, and prairie edges from New Jersey and Pennsylvania south to Georgia and west to Missouri and Kansas.',
    currentDistribution: 'Widespread in native range; extensively cultivated across North America and Europe.',
  }),

  exactSubdivisionRangeRecord({
    scientificName: 'Asclepias syriaca',
    commonNames: ['Common Milkweed', 'Silkweed'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to eastern and central North America in open fields, roadsides, and prairie edges'],
    regions: [
      { code: 'US-AR', status: 'native' }, { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' }, { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and central North America'],
    origin: 'Native to eastern and central North America.',
    nativeHabitat: 'Open fields, roadsides, disturbed areas, prairies, and woodland edges from southern Canada south to Georgia and west to Kansas.',
    currentDistribution: 'Widespread in native range; extent of occurrence has declined significantly in agricultural areas due to herbicide use in corn and soybean fields.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Mertensia virginica',
    commonNames: ['Virginia Bluebells', 'Virginia Cowslip'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; spring ephemeral native to moist floodplain forests and woodland bottomlands of eastern and central North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-DC', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and central North America'],
    origin: 'Native to eastern and central North America.',
    nativeHabitat: 'Moist floodplain forests, rich woodland bottomlands, stream banks, and alluvial soils; a spring ephemeral that blooms and sets seed before tree canopy fully leafs out.',
    currentDistribution: 'Native range largely intact; locally reduced in areas where riparian woodland has been cleared or flood-control altered streamside habitats.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Geranium maculatum',
    commonNames: ['Wild Geranium', 'Spotted Geranium', 'Cranesbill'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to woodland understories and forest edges across eastern North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America.',
    nativeHabitat: 'Woodland understories, forest edges, thickets, and shaded roadsides in well-drained to moderately moist soils.',
    currentDistribution: 'Widespread and stable throughout native range; commonly found in intact deciduous forest understories.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Eupatorium perfoliatum',
    commonNames: ['Boneset', 'Common Boneset', 'Thoroughwort'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to moist meadows, wetland margins, and streambanks across eastern North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' }, { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and central North America'],
    origin: 'Native to eastern and central North America.',
    nativeHabitat: 'Wet meadows, marshes, streambanks, pond margins, and moist roadside ditches; thrives in consistently moist to wet soils.',
    currentDistribution: 'Widespread in native range; populations stable where wetland habitats are preserved.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Lobelia siphilitica',
    commonNames: ['Blue Lobelia', 'Great Blue Lobelia', 'Great Lobelia'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to moist woods, streambanks, and wet meadows across eastern and central North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and central North America'],
    origin: 'Native to eastern and central North America.',
    nativeHabitat: 'Moist woods, stream banks, wet meadows, seeps, and pond margins; prefers consistently moist to wet soils in partial to full sun.',
    currentDistribution: 'Widespread in native range; stable where moist habitats remain intact.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Chamaecrista fasciculata',
    commonNames: ['Partridge Pea', 'Sleeping Plant', 'Prairie Senna'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native annual legume of prairies, open woodlands, roadsides, and sandy soils across eastern and central North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and central North America'],
    origin: 'Native to eastern and central North America.',
    nativeHabitat: 'Prairies, open woodlands, roadsides, disturbed sandy or dry soils; a nitrogen-fixing annual that thrives in poor, well-drained soils.',
    currentDistribution: 'Widespread in native range; a common roadside and disturbed-area colonizer that self-sows prolifically.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Hibiscus moscheutos',
    commonNames: ['Swamp Rose Mallow', 'Crimsoneyed Rosemallow', 'Hardy Hibiscus'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to coastal marshes, swamps, and wet meadows of eastern North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America.',
    nativeHabitat: 'Coastal marshes, swamps, wet meadows, streambanks, and pond margins; tolerates periodic flooding and thrives in wet to saturated soils.',
    currentDistribution: 'Native range intact along the Atlantic and Gulf coasts; widely cultivated outside native range as a garden ornamental.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Chelone glabra',
    commonNames: ['White Turtlehead', 'Turtlehead', 'Balmony'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to streambanks, wet woods, and boggy meadows across eastern North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America.',
    nativeHabitat: 'Streambanks, wet woods, boggy meadows, and seeps; requires consistently moist to wet soils and tolerates periodic flooding.',
    currentDistribution: 'Widespread in eastern North America; populations stable where streamside and wetland habitats remain intact.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Helianthus maximiliani',
    commonNames: ['Maximilian Sunflower', 'Maximilian\'s Sunflower'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to tallgrass and mixed-grass prairies of the Great Plains and central North America'],
    regions: [
      { code: 'US-AR', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-LA', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-ND', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WY', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Central North America', 'Great Plains'],
    origin: 'Native to the Great Plains and central North America.',
    nativeHabitat: 'Tallgrass and mixed-grass prairies, roadsides, and disturbed open areas; adapted to dry to mesic soils and full sun.',
    currentDistribution: 'Widespread in Great Plains native range; widely cultivated and naturalized beyond native range as a wildlife garden plant.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Allium tricoccum',
    commonNames: ['Ramps', 'Wild Leek', 'Wild Garlic'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to moist deciduous forests and streamside woodland floors of eastern North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to moist deciduous forests of eastern North America, centered in the Appalachian Mountains.',
    nativeHabitat: 'Rich, moist deciduous forest floors, streamside woods, and shaded bottomlands under maple, beech, and basswood in humus-rich slightly acidic soils.',
    currentDistribution: 'Native range intact but wild populations declining in accessible areas due to overharvesting for culinary markets; cultivation increasing as a sustainable alternative.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Apios americana',
    commonNames: ['Groundnut', 'Hopniss', 'Indian Potato'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to moist thickets, streambanks, and woodland margins of eastern and central North America'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern and central North America'],
    origin: 'Native to eastern and central North America, from New Brunswick and Manitoba south to Florida and Texas.',
    nativeHabitat: 'Moist thickets, streambanks, woodland margins, and low-lying floodplain areas; twines through shrubs and low trees.',
    currentDistribution: 'Widespread in native range; naturalized in parts of Europe following 19th-century introduction attempts; increasingly cultivated in food forest and permaculture plantings.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Allium cernuum',
    commonNames: ['Nodding Onion', 'Nodding Wild Onion'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; one of the most widely distributed native alliums in North America, native to rocky slopes, prairie edges, and open woodland clearings'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' }, { code: 'US-CA', status: 'native' },
      { code: 'US-CO', status: 'native' }, { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NM', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-UT', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
      { code: 'CA-AB', status: 'native' }, { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['North America'],
    origin: 'Native across most of North America, one of the most widely distributed native alliums on the continent.',
    nativeHabitat: 'Rocky slopes, prairie edges, open woodland clearings, and dry to mesic meadows in full sun to light shade; tolerates thin, rocky soils.',
    currentDistribution: 'Widespread and stable throughout its native North American range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Claytonia perfoliata',
    commonNames: ["Miner's Lettuce", 'Winter Purslane', 'Springbeauty'],
    source: 'BONAP',
    sourceNotes: ["BONAP 2014 county-level data; native to cool, moist woodland and disturbed ground of western North America; naturalized widely elsewhere"],
    regions: [
      { code: 'US-AK', status: 'native' }, { code: 'US-AZ', status: 'native' },
      { code: 'US-CA', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-ID', status: 'native' }, { code: 'US-MT', status: 'native' },
      { code: 'US-NM', status: 'native' }, { code: 'US-NV', status: 'native' },
      { code: 'US-OR', status: 'native' }, { code: 'US-UT', status: 'native' },
      { code: 'US-WA', status: 'native' }, { code: 'US-WY', status: 'native' },
      { code: 'CA-BC', status: 'native' },
    ],
    broaderNativeRange: ['Western North America'],
    origin: 'Native to western North America, from British Columbia south through the Pacific coast states and mountain west.',
    nativeHabitat: 'Moist, shaded woodland clearings, streambanks, disturbed ground, and grassy slopes in cool, damp, partially shaded conditions.',
    currentDistribution: 'Native to western North America; naturalized widely in Europe, eastern North America, and temperate regions worldwide as a garden escape.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Matteuccia struthiopteris',
    commonNames: ['Ostrich Fern', 'Fiddlehead Fern'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; circumboreal species native to moist woodland and streamside habitats of northern North America, Europe, and Asia'],
    regions: [
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MT', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-AB', status: 'native' }, { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Northern and eastern North America', 'Circumpolar'],
    origin: 'Circumboreal; native to northern and eastern North America, Europe, and Asia.',
    nativeHabitat: 'Moist to wet woodland edges, streambanks, floodplain forests, and damp shaded ravines in rich, well-watered soils.',
    currentDistribution: 'Widespread in native circumboreal range; widely cultivated as a garden ornamental throughout temperate regions.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Vaccinium macrocarpon',
    commonNames: ['Cranberry', 'American Cranberry', 'Large Cranberry'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; native to sphagnum bogs and acidic wetlands of eastern North America'],
    regions: [
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-NB', status: 'native' }, { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' }, { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America, from Newfoundland south to the Atlantic and Gulf coastal plain and inland to the Great Lakes.',
    nativeHabitat: 'Sphagnum bogs, peat wetlands, acidic lake margins, and open wet heathland in very acidic, waterlogged, low-nutrient soils.',
    currentDistribution: 'Native range largely intact in protected wetlands; commercially cultivated in flooded bogs in Massachusetts, Wisconsin, and the Pacific Northwest.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Vaccinium vitis-idaea',
    commonNames: ['Lingonberry', 'Mountain Cranberry', 'Foxberry'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; circumpolar species native to boreal forests and arctic heaths; North American populations are subsp. minus'],
    regions: [
      { code: 'US-AK', status: 'native' }, { code: 'US-CT', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'CA-AB', status: 'native' }, { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-NT', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' }, { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' }, { code: 'CA-YT', status: 'native' },
    ],
    broaderNativeRange: ['Northern North America', 'Circumpolar'],
    origin: 'Circumpolar; native to boreal forests and arctic heaths of northern North America, Europe, and Asia.',
    nativeHabitat: 'Boreal conifer forests, heath and tundra, acidic peat bogs, rocky outcrops, and open woodland floors in cool, acidic, low-nutrient soils.',
    currentDistribution: 'Widespread in circumboreal native range; cultivated primarily in Scandinavia and increasingly in North American gardens.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Prunus virginiana',
    commonNames: ['Chokecherry', 'Western Chokecherry'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; one of the most widely distributed native trees in North America, found from the subarctic south to northern Mexico'],
    regions: [
      { code: 'US-AK', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' }, { code: 'US-CA', status: 'native' },
      { code: 'US-CO', status: 'native' }, { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NM', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' }, { code: 'US-WY', status: 'native' },
      { code: 'CA-AB', status: 'native' }, { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['North America'],
    origin: 'Native across most of North America, from the Atlantic coast to the Pacific and from the subarctic south to northern Mexico.',
    nativeHabitat: 'Forest edges, stream banks, thickets, and roadsides in a wide range of soils; highly adaptable to dry to moist conditions.',
    currentDistribution: 'One of the most widely distributed native trees in North America; abundant throughout its range.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Rhododendron viscosum',
    commonNames: ['Swamp Azalea', 'Clammy Azalea'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; coastal plain and Piedmont distribution from Maine to Florida, inland along Appalachians to Tennessee and Ohio'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to the eastern United States, from Maine south along the coastal plain and Piedmont to Florida and west along the Appalachians to Tennessee, Ohio, and the Gulf Coast states.',
    nativeHabitat: 'Swamps, bogs, stream banks, wet woodland edges, and acidic seepage slopes in moist to wet, acidic, humus-rich soils.',
    currentDistribution: 'Widespread in its eastern North American native range; widely cultivated in wet woodland gardens and native plant landscapes throughout the eastern US.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Viburnum trilobum',
    commonNames: ['American Cranberrybush', 'Highbush Cranberry', 'American Highbush Cranberry'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; primarily northern North America from Pacific Northwest through Great Lakes and New England across boreal Canada'],
    regions: [
      { code: 'US-CT', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MT', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OR', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' }, { code: 'US-WY', status: 'native' },
      { code: 'CA-AB', status: 'native' }, { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Northern North America'],
    origin: 'Native across northern North America, from the Pacific Northwest through the Great Plains and Great Lakes to New England and across boreal Canada.',
    nativeHabitat: 'Moist forest edges, stream banks, lake shores, floodplain thickets, and rich woodland margins; tolerates periodic flooding.',
    currentDistribution: 'Widespread in native range; widely cultivated as an ornamental and edible shrub throughout temperate North America.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Cornus sericea',
    commonNames: ['Red-Osier Dogwood', 'Red-Twig Dogwood', 'Western Dogwood'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; one of the most widely distributed native shrubs in North America, ranging from subarctic Alaska to the southern US'],
    regions: [
      { code: 'US-AK', status: 'native' }, { code: 'US-AZ', status: 'native' },
      { code: 'US-CA', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MT', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' }, { code: 'US-WY', status: 'native' },
      { code: 'CA-AB', status: 'native' }, { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-NT', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' }, { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' }, { code: 'CA-YT', status: 'native' },
    ],
    broaderNativeRange: ['North America'],
    origin: 'One of the most widely distributed native shrubs in North America; native from Alaska south to California and New Mexico in the west, and from the subarctic east to the Atlantic coast.',
    nativeHabitat: 'Stream banks, lake margins, wet meadows, floodplains, wet thickets, moist forest edges, and disturbed wet ground; highly tolerant of flooding and saturated soils.',
    currentDistribution: 'Abundant throughout most of its vast native range; widely used in restoration plantings, rain gardens, and stream bank stabilization projects.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Ceanothus americanus',
    commonNames: ['New Jersey Tea', 'Redroot', 'Wild Snowball'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; eastern and central North America from Gulf Coast to southern Quebec and Ontario, west to the Great Plains'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern and central North America, from the Gulf Coast north to southern Quebec and Ontario, and west to the Great Plains.',
    nativeHabitat: 'Dry to xeric open woodlands, forest edges, prairies, rocky slopes, and barrens in well-drained to dry, infertile soils.',
    currentDistribution: 'Widespread in native range; increasingly cultivated as a drought-tolerant pollinator shrub in native plant gardens throughout the eastern US and lower Midwest.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Verbena hastata',
    commonNames: ['Blue Vervain', 'Swamp Verbena', 'American Blue Vervain'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; very wide distribution across most of the continental US and southern Canada, common in wet disturbed habitats'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' }, { code: 'US-CA', status: 'native' },
      { code: 'US-CO', status: 'native' }, { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' }, { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-MT', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-OR', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
      { code: 'CA-BC', status: 'native' }, { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['North America'],
    origin: 'Native across most of North America, from British Columbia to Nova Scotia south through the eastern and central United States to Florida and the Gulf Coast, and west to the Rocky Mountains.',
    nativeHabitat: 'Wet meadows, stream banks, marsh edges, lake shores, floodplains, and moist disturbed ground in full sun with consistently moist to wet soils.',
    currentDistribution: 'Widespread and common throughout its native range; increasingly used in rain gardens, wet bioswales, and naturalized meadow plantings.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Senna hebecarpa',
    commonNames: ['Wild Senna', 'American Senna'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; eastern North America from New England and Great Lakes south to Georgia and west to the Great Plains'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to the eastern United States, from New England and the Great Lakes south to Georgia and west to the Great Plains.',
    nativeHabitat: 'Moist to moderately dry open woodlands, woodland edges, thickets, stream banks, and prairie margins; often in disturbed but fertile soils with full sun to light shade.',
    currentDistribution: 'Widespread in eastern North America; increasingly used in native plant gardens and pollinator plantings throughout the eastern US.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Dalea purpurea',
    commonNames: ['Purple Prairie Clover', 'Violet Prairie Clover'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; central North American prairies from Canadian prairie provinces south through Great Plains and Midwest to Texas'],
    regions: [
      { code: 'US-AR', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MT', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NM', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WY', status: 'native' },
      { code: 'CA-AB', status: 'native' }, { code: 'CA-MB', status: 'native' },
      { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Central North American Prairies'],
    origin: 'Native to the central North American prairies from the southern Canadian prairie provinces south through the Great Plains and Midwest to Texas and the Gulf Coast margins.',
    nativeHabitat: 'Dry to moderately dry prairies, open barrens, glades, roadsides, and sandy or rocky open ground; thrives in lean, well-drained soils with full sun.',
    currentDistribution: 'Widespread across the central prairies from Alberta and Saskatchewan south to Texas and east into the western Midwest; increasingly used in native meadow and prairie restoration plantings.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Dalea candida',
    commonNames: ['White Prairie Clover'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; central North American prairies from Canadian prairie provinces south through Great Plains and Midwest to Texas; scattered eastern records in TN'],
    regions: [
      { code: 'US-AR', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' }, { code: 'US-MO', status: 'native' },
      { code: 'US-MT', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NM', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WY', status: 'native' },
      { code: 'CA-AB', status: 'native' }, { code: 'CA-MB', status: 'native' },
      { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Central North American Prairies'],
    origin: 'Native to the central North American prairies from the southern Canadian prairies south through the Great Plains and Midwest to Texas, with scattered eastern records into Tennessee and Alabama.',
    nativeHabitat: 'Dry to moderately dry prairies, open woodlands, glades, roadsides, and rocky or sandy open ground; full sun and lean, well-drained soils.',
    currentDistribution: 'Widespread in the central prairies from Canada south to Texas; frequently planted together with purple prairie clover in native meadow and prairie restoration projects.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Physostegia virginiana',
    commonNames: ['Obedient Plant', 'False Dragonhead'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; eastern and central North America from Maritime provinces south to Florida and Texas'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Central North America'],
    origin: 'Native to eastern and central North America from the Maritime provinces and Quebec south through the eastern United States to Florida and Texas, and west through the prairie states to the Dakotas and Nebraska.',
    nativeHabitat: 'Moist prairies, prairie margins, thicket borders, streambanks, floodplain forest edges, and open moist woodland clearings; tolerates seasonal flooding; prefers moist to wet, fertile soils in full sun to light shade.',
    currentDistribution: 'Widespread in eastern and central North America; cultivated widely beyond its native range; numerous cultivars selected for compact habit.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Helenium autumnale',
    commonNames: ['Sneezeweed', 'Common Sneezeweed', 'Fall Sneezeweed'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; one of the most widespread native perennials in North America east of the Rockies'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CA', status: 'native' }, { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-MT', status: 'native' }, { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' }, { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' }, { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' }, { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' }, { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' }, { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
      { code: 'CA-AB', status: 'native' }, { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NS', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' }, { code: 'CA-SK', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Central North America'],
    origin: 'Native to most of North America east of the Rockies, from southern Canada south through the eastern and central United States to Florida and the Gulf Coast.',
    nativeHabitat: 'Moist to wet open areas including streambanks, floodplains, wet meadows, prairie swales, roadsides, and moist forest edges; tolerates seasonal flooding and clay soils.',
    currentDistribution: 'One of the most widespread native perennials in North America; occurs naturally across most of the eastern half of the continent and is widely cultivated in native and traditional perennial gardens.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Silphium perfoliatum',
    commonNames: ['Cup Plant'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; central and eastern United States and adjacent Canada from Great Lakes south to Georgia and west to the Dakotas'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' }, { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' }, { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' }, { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' }, { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' }, { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' }, { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-SD', status: 'native' }, { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-ON', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Central North America'],
    origin: 'Native to the central and eastern United States and adjacent Canada, from the Great Lakes and New England south to Georgia and west through the prairie states to the Dakotas.',
    nativeHabitat: 'Moist prairies, prairie margins, floodplain forests, streambanks, roadsides, and disturbed fertile ground with full sun to light shade; thrives in rich, moist soils.',
    currentDistribution: 'Widespread in the central and eastern US; increasingly used in rain gardens, native meadows, and prairie restoration plantings across its range and beyond.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Packera aurea',
    commonNames: ['Golden Ragwort', 'Golden Groundsel', 'Life Root'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; eastern North America from Maritime provinces and Labrador south through eastern US to Georgia and west to Nebraska'],
    regions: [
      { code: 'US-AL', status: 'native' }, { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' }, { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' }, { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' }, { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' }, { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' }, { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' }, { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' }, { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' }, { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' }, { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' }, { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' }, { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' }, { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' }, { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' }, { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' }, { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' }, { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' }, { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' }, { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Native to eastern North America from the Maritime provinces and Labrador south through the eastern United States to Georgia, Florida, and west to Nebraska and the eastern Great Plains.',
    nativeHabitat: 'Moist to wet woodlands, woodland edges, streambanks, floodplain forests, moist meadows, and shaded slopes; thrives in consistently moist, rich soils in partial to full shade.',
    currentDistribution: 'Common throughout eastern North America; widely used as a native groundcover in shaded and moist woodland garden settings.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Amsonia hubrichtii',
    commonNames: ['Arkansas Bluestar', 'Hubricht\'s Bluestar'],
    source: 'BONAP',
    sourceNotes: ['BONAP 2014 county-level data; extremely restricted native range confined primarily to Ouachita River valley in Arkansas with marginal records in Oklahoma and Missouri'],
    regions: [
      { code: 'US-AR', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-OK', status: 'native' },
    ],
    broaderNativeRange: ['Ouachita Region', 'South-Central United States'],
    origin: 'Native to a small area of the Ouachita River valley in west-central Arkansas and adjacent Oklahoma and Missouri; one of the most geographically restricted native perennials in North American horticulture.',
    nativeHabitat: 'Rocky, shallow, well-drained soils along and near riverbanks, rocky bluffs, and open woodland edges in the Ouachita region; full sun to light shade on thin, nutrient-poor substrates.',
    currentDistribution: 'Widely cultivated across North America and internationally, far beyond its narrow native range; wild populations are concentrated in the Ouachita River drainage in Arkansas and immediately adjacent states.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Itea virginica',
    commonNames: ['Virginia Sweetspire', 'Virginia willow', 'tassel-white'],
    source: 'USDA PLANTS',
    sourceNotes: ['USDA PLANTS Database native status records for Itea virginica; state-level native occurrences across the eastern and southeastern United States.'],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Southeastern United States'],
    origin: 'Virginia Sweetspire is native to the eastern United States from New Jersey and New York south through the Atlantic Coastal Plain and Piedmont to Florida and west to Texas and Missouri.',
    nativeHabitat: 'Streambanks, floodplain forests, pond margins, wet woodland edges, and moist low areas; tolerates periodic flooding and heavy clay soils in partial to full shade.',
    currentDistribution: 'Native across much of the eastern and southeastern United States; widely cultivated as an ornamental and native landscaping shrub throughout temperate eastern North America.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Symphoricarpos orbiculatus',
    commonNames: ['Coralberry', 'Indian currant', 'buckbrush'],
    source: 'USDA PLANTS',
    sourceNotes: ['USDA PLANTS Database native status records for Symphoricarpos orbiculatus; state and province-level native occurrences across eastern and central North America.'],
    regions: [
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-ON', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America', 'Central North America'],
    origin: 'Coralberry is native across eastern and central North America from Ontario and the Atlantic Coast west to the Great Plains and south to the Gulf Coast and Texas.',
    nativeHabitat: 'Rocky woodlands, woodland edges, roadsides, slopes, thickets, and disturbed ground; tolerates dry, rocky, clay, and shaded conditions across a wide range of site types.',
    currentDistribution: 'Common across eastern and central North America within its native range; occasionally cultivated as an ornamental and wildlife plant beyond its natural distribution.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Sisyrinchium angustifolium',
    commonNames: ['blue-eyed grass', 'narrow-leaved blue-eyed grass'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'USDA PLANTS Database native status records for Sisyrinchium angustifolium; eastern North American native distribution across Maritime Canada, Quebec, Ontario, and eastern through central United States.',
    ],
    regions: [
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Blue-eyed grass is native to eastern North America, from the Maritime provinces of Canada and Quebec south through New England and the Mid-Atlantic, across the Appalachians and into the eastern Great Plains, and throughout the southeastern United States to Florida.',
    nativeHabitat: 'Moist to moderately dry meadows, stream banks, open woodland edges, wet prairies, and roadside ditches; thrives in full sun to light shade with consistent spring moisture.',
    currentDistribution: 'Occurs naturally throughout eastern North America within its native range; widely cultivated in native plant gardens and ecological restoration plantings.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Prunella vulgaris var. lanceolata',
    commonNames: ['self heal', 'heal-all', 'common self-heal', 'self-heal'],
    source: 'USDA PLANTS',
    sourceNotes: [
      'USDA PLANTS Database native status records for Prunella vulgaris var. lanceolata; the native North American variety distinguished from the introduced European var. vulgaris. Native across most of Canada and the contiguous United States.',
    ],
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-AK', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' },
      { code: 'US-CA', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' },
      { code: 'US-NV', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['North America', 'Circumboreal'],
    origin: 'The native North American variety (var. lanceolata) is indigenous across most of the continent, from Alaska and all Canadian provinces south through the contiguous United States. The species as a whole is circumboreal, occurring natively across Europe and Asia as well.',
    nativeHabitat: 'Moist to moderately dry meadows, lawns, roadsides, open woodland edges, disturbed ground, and stream banks; highly tolerant of mowing, foot traffic, and competition.',
    currentDistribution: 'Widespread across North America within its native range; the introduced European var. vulgaris also occurs in much of the same territory, making both varieties common companions in disturbed habitats continent-wide.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Phlox subulata',
    commonNames: ['creeping phlox', 'moss phlox', 'moss pink'],
    source: 'USDA PLANTS Database',
    sourceNotes: [
      'Native range verified against USDA PLANTS Database records for Phlox subulata; native to rocky outcrops and thin-soiled slopes of eastern and central North America.',
    ],
    regions: [
      { code: 'CA-ON', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
    ],
    broaderNativeRange: ['Eastern North America'],
    origin: 'Creeping phlox is native to rocky outcrops, dry open slopes, and thin-soiled sites in eastern and central North America, from New England and the mid-Atlantic south through the Appalachians into the piedmont, west into the Great Lakes region and parts of the central states.',
    nativeHabitat: 'Rocky ledges, sandstone and shale outcrops, dry open slopes, thin soils over bedrock, and open woodlands with excellent drainage; naturally found on south-facing exposures where frost heaving and drought are facts of life.',
    currentDistribution: 'Occurs naturally throughout its native range in eastern North America; extensively cultivated well beyond that range in gardens across the United States, Canada, and Europe.',
  }),
  exactSubdivisionRangeRecord({
    scientificName: 'Galium aparine',
    commonNames: ['cleavers', 'sticky willy', 'goosegrass', 'catchweed'],
    source: 'USDA PLANTS Database',
    sourceNotes: [
      'Circumboreal species native across most of North America per USDA PLANTS Database; native status confirmed across all contiguous states and most Canadian provinces.',
    ],
    regions: [
      { code: 'CA-AB', status: 'native' },
      { code: 'CA-BC', status: 'native' },
      { code: 'CA-MB', status: 'native' },
      { code: 'CA-NB', status: 'native' },
      { code: 'CA-NL', status: 'native' },
      { code: 'CA-NS', status: 'native' },
      { code: 'CA-ON', status: 'native' },
      { code: 'CA-PE', status: 'native' },
      { code: 'CA-QC', status: 'native' },
      { code: 'CA-SK', status: 'native' },
      { code: 'US-AK', status: 'native' },
      { code: 'US-AL', status: 'native' },
      { code: 'US-AR', status: 'native' },
      { code: 'US-AZ', status: 'native' },
      { code: 'US-CA', status: 'native' },
      { code: 'US-CO', status: 'native' },
      { code: 'US-CT', status: 'native' },
      { code: 'US-DC', status: 'native' },
      { code: 'US-DE', status: 'native' },
      { code: 'US-FL', status: 'native' },
      { code: 'US-GA', status: 'native' },
      { code: 'US-IA', status: 'native' },
      { code: 'US-ID', status: 'native' },
      { code: 'US-IL', status: 'native' },
      { code: 'US-IN', status: 'native' },
      { code: 'US-KS', status: 'native' },
      { code: 'US-KY', status: 'native' },
      { code: 'US-LA', status: 'native' },
      { code: 'US-MA', status: 'native' },
      { code: 'US-MD', status: 'native' },
      { code: 'US-ME', status: 'native' },
      { code: 'US-MI', status: 'native' },
      { code: 'US-MN', status: 'native' },
      { code: 'US-MO', status: 'native' },
      { code: 'US-MS', status: 'native' },
      { code: 'US-MT', status: 'native' },
      { code: 'US-NC', status: 'native' },
      { code: 'US-ND', status: 'native' },
      { code: 'US-NE', status: 'native' },
      { code: 'US-NH', status: 'native' },
      { code: 'US-NJ', status: 'native' },
      { code: 'US-NM', status: 'native' },
      { code: 'US-NY', status: 'native' },
      { code: 'US-OH', status: 'native' },
      { code: 'US-OK', status: 'native' },
      { code: 'US-OR', status: 'native' },
      { code: 'US-PA', status: 'native' },
      { code: 'US-RI', status: 'native' },
      { code: 'US-SC', status: 'native' },
      { code: 'US-SD', status: 'native' },
      { code: 'US-TN', status: 'native' },
      { code: 'US-TX', status: 'native' },
      { code: 'US-UT', status: 'native' },
      { code: 'US-VA', status: 'native' },
      { code: 'US-VT', status: 'native' },
      { code: 'US-WA', status: 'native' },
      { code: 'US-WI', status: 'native' },
      { code: 'US-WV', status: 'native' },
      { code: 'US-WY', status: 'native' },
    ],
    broaderNativeRange: ['North America', 'Europe', 'Asia', 'Circumboreal'],
    origin: 'Cleavers is a circumboreal species native across most of North America from Alaska and all Canadian provinces south through the contiguous United States, as well as independently native across Europe and temperate Asia.',
    nativeHabitat: 'Woodland edges, hedgerows, moist and shaded disturbed ground, riparian margins, and garden edges; thrives in partial shade and scrambles through established vegetation using hooked stems.',
    currentDistribution: 'One of the most widely distributed annual herbs in the temperate Northern Hemisphere; occurs naturally throughout its vast circumboreal native range and is present as a garden weed in many additional areas.',
  }),
];

function key(value: string | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[._]/g, ' ')
    .replace(/\s+/g, ' ');
}

function findSourceRecord(plant: Plant): { record: NativeRangeSourceRecord; matchType: MatchType } | null {
  const extracted = extractSubdivisionNativeRange(plant);
  if (extracted) return extracted;

  const scientificName = key(plant.scientificName);
  const byScientificName = AUTHORITATIVE_NATIVE_RANGE_IMPORTS.find(record => key(record.scientificName) === scientificName);
  if (byScientificName) return { record: byScientificName, matchType: 'scientific-name' };

  const commonName = key(plant.name);
  const byCommonName = AUTHORITATIVE_NATIVE_RANGE_IMPORTS.find(record =>
    record.commonNames.map(key).includes(commonName),
  );

  return byCommonName ? { record: byCommonName, matchType: 'common-name' } : null;
}

export function extractSubdivisionNativeRange(plant: Plant): { record: NativeRangeSourceRecord; matchType: MatchType } | null {
  const scientificName = key(plant.scientificName);
  if (!scientificName) return null;

  const exactSourceRecords = [
    ...AUTHORITATIVE_EXACT_SUBDIVISION_IMPORTS,
    ...AUTHORITATIVE_NATIVE_RANGE_IMPORTS,
  ];
  const record = exactSourceRecords.find(candidate =>
    key(candidate.scientificName) === scientificName &&
    candidate.sourceReliability === 'authoritative' &&
    candidate.coverageLevel !== 'broad-only' &&
    candidate.regions.length > 0,
  );

  return record ? { record, matchType: 'scientific-name' } : null;
}

export function lookupNativeRangeForPlant(plant: Plant): NativeRangeLookupResult {
  const match = findSourceRecord(plant);
  if (!match) {
    return {
      found: false,
      reason: 'No authoritative native-range import record is available for this plant.',
    };
  }

  const { record, matchType } = match;
  return {
    found: true,
    source: record.source,
    sourceNotes: record.sourceNotes,
    matchType,
    sourceReliability: record.sourceReliability,
    coverageLevel: record.coverageLevel,
    distributionComplexity: record.distributionComplexity,
    regions: record.regions,
    broaderNativeRange: record.broaderNativeRange,
    origin: record.origin,
    nativeHabitat: record.nativeHabitat,
    currentDistribution: record.currentDistribution,
  };
}

export function computeNativeRangeConfidence(
  lookup: NativeRangeLookupResult,
): PlantNativeRange['importConfidence'] {
  if (!lookup.found) return 'low';
  if (lookup.matchType !== 'scientific-name') return 'low';

  const hasLineageAmbiguity =
    lookup.distributionComplexity === 'domesticate' ||
    lookup.distributionComplexity === 'hybrid' ||
    lookup.distributionComplexity === 'aggregate' ||
    lookup.distributionComplexity === 'cultivar-group' ||
    lookup.distributionComplexity === 'uncertain';

  if (hasLineageAmbiguity) return 'low';

  if (
    lookup.sourceReliability === 'authoritative' &&
    lookup.coverageLevel !== 'broad-only' &&
    (lookup.regions?.length ?? 0) > 0
  ) {
    return 'high';
  }

  if (
    lookup.sourceReliability === 'authoritative' &&
    lookup.coverageLevel === 'broad-only'
  ) {
    return 'medium';
  }

  return 'low';
}

export function normalizeImportedNativeRange(
  lookup: NativeRangeLookupResult,
  attemptedAt: string = new Date().toISOString(),
): PlantNativeRange {
  if (!lookup.found) {
    return {
      reviewStatus: 'unreviewed',
      importStatus: 'failed',
      importConfidence: 'low',
      importAttemptedAt: attemptedAt,
      sourceNotes: lookup.reason ? [lookup.reason] : undefined,
      regions: [],
    };
  }

  return {
    reviewStatus: 'unreviewed',
    importSource: lookup.source,
    importConfidence: computeNativeRangeConfidence(lookup),
    importStatus: 'imported',
    importAttemptedAt: attemptedAt,
    ...(lookup.sourceNotes && lookup.sourceNotes.length > 0 ? { sourceNotes: lookup.sourceNotes } : {}),
    regions: lookup.regions ?? [],
    ...(lookup.broaderNativeRange ? { broaderNativeRange: lookup.broaderNativeRange } : {}),
    ...(lookup.origin ? { origin: lookup.origin } : {}),
    ...(lookup.nativeHabitat ? { nativeHabitat: lookup.nativeHabitat } : {}),
    ...(lookup.currentDistribution ? { currentDistribution: lookup.currentDistribution } : {}),
  };
}

export function enrichPlantNativeRange(
  plant: Plant,
  options: { force?: boolean; attemptedAt?: string } = {},
): Plant {
  const existing = plant.nativeRange;
  if (existing?.reviewStatus === 'reviewed') return plant;
  if (!options.force && existing?.importStatus === 'imported') return plant;

  const lookup = lookupNativeRangeForPlant(plant);
  return {
    ...plant,
    nativeRange: normalizeImportedNativeRange(lookup, options.attemptedAt),
  };
}

function stripNativeRange<T extends Plant>(plant: T): T {
  const next = { ...plant };
  delete next.nativeRange;
  return next;
}

export function enrichPlantNativeRangeForDisplay<T extends Plant>(
  plant: T,
  options: { attemptedAt?: string } = {},
): T {
  if (plant.nativeRange) {
    if (!nativeRangeHasDisplayableData(plant.nativeRange)) {
      return stripNativeRange(plant);
    }
    // When prose exists but regions are empty, merge authoritative subdivision data so
    // native filtering works for reviewed built-in plants with hand-authored prose.
    if (plant.nativeRange.regions.length === 0) {
      const extracted = extractSubdivisionNativeRange(plant);
      if (extracted && extracted.record.regions.length > 0) {
        return {
          ...plant,
          nativeRange: {
            ...plant.nativeRange,
            regions: extracted.record.regions,
          },
        };
      }
    }
    return plant;
  }

  const lookup = lookupNativeRangeForPlant(plant);
  if (!lookup.found) return plant;
  const nativeRange = normalizeImportedNativeRange(lookup, options.attemptedAt);
  if (!nativeRangeHasDisplayableData(nativeRange)) return plant;

  return {
    ...plant,
    nativeRange,
  };
}

export function backfillNativeRangeForAllPlants(
  plants: Plant[] = PLANTS,
  options: {
    force?: boolean;
    attemptedAt?: string;
    logger?: (result: NativeRangeBackfillResult) => void;
  } = {},
): { plants: Plant[]; results: NativeRangeBackfillResult[]; report: NativeRangeCoverageReport } {
  const attemptedAt = options.attemptedAt ?? new Date().toISOString();
  const results: NativeRangeBackfillResult[] = [];
  const enrichedPlants = plants.map(plant => {
    if (plant.nativeRange?.reviewStatus === 'reviewed') {
      const result: NativeRangeBackfillResult = {
        plantId: plant.id,
        plantName: plant.name,
        status: 'skipped_reviewed',
        reason: 'Existing reviewed nativeRange was preserved.',
        plant,
      };
      results.push(result);
      options.logger?.(result);
      return plant;
    }

    if (!options.force && plant.nativeRange?.importStatus === 'imported') {
      const result: NativeRangeBackfillResult = {
        plantId: plant.id,
        plantName: plant.name,
        status: 'skipped_existing_import',
        importSource: plant.nativeRange.importSource,
        importConfidence: plant.nativeRange.importConfidence,
        reason: 'Existing imported nativeRange was preserved.',
        plant,
      };
      results.push(result);
      options.logger?.(result);
      return plant;
    }

    const enriched = enrichPlantNativeRange(plant, { force: options.force, attemptedAt });
    const status: NativeRangeBackfillResult['status'] = enriched.nativeRange?.importStatus === 'imported'
      ? 'imported'
      : 'failed';
    const result: NativeRangeBackfillResult = {
      plantId: plant.id,
      plantName: plant.name,
      status,
      importSource: enriched.nativeRange?.importSource,
      importConfidence: enriched.nativeRange?.importConfidence,
      reason: enriched.nativeRange?.sourceNotes?.[0],
      plant: enriched,
    };
    results.push(result);
    options.logger?.(result);
    return enriched;
  });

  return {
    plants: enrichedPlants,
    results,
    report: getNativeRangeCoverageReport(enrichedPlants),
  };
}

export function getNativeRangeCoverageReport(plants: Plant[] = PLANTS): NativeRangeCoverageReport {
  return plants.reduce<NativeRangeCoverageReport>(
    (report, plant) => {
      report.totalPlants += 1;
      const nativeRange = enrichPlantNativeRangeForDisplay(plant).nativeRange;
      if (nativeRange?.reviewStatus === 'reviewed') {
        report.reviewed += 1;
        if (nativeRangeCanMatchSubdivision(nativeRange)) {
          report.manually_reviewed_exact_match_eligible += 1;
        }
      } else if (nativeRange?.importStatus === 'imported') {
        const isAutoExactMatchEligible =
          nativeRangeCanMatchSubdivision(nativeRange) &&
          nativeRange.importConfidence === 'high';

        if (nativeRange.importConfidence === 'high') {
          report.auto_imported_high_confidence += 1;
          if (isAutoExactMatchEligible) {
            report.auto_imported_high_confidence_exact_match_eligible += 1;
          }
        } else if (nativeRange.importConfidence === 'medium') {
          report.auto_imported_medium_confidence += 1;
        } else {
          report.auto_imported_low_confidence += 1;
        }

        if (!isAutoExactMatchEligible || !nativeRangeHasNativeSubdivisionRows(nativeRange)) {
          report.auto_imported_display_only += 1;
        }
      } else if (nativeRange?.importStatus === 'failed') {
        report.failed_or_missing += 1;
      } else {
        report.failed_or_missing += 1;
      }

      return report;
    },
    {
      totalPlants: 0,
      reviewed: 0,
      manually_reviewed_exact_match_eligible: 0,
      auto_imported_high_confidence: 0,
      auto_imported_high_confidence_exact_match_eligible: 0,
      auto_imported_medium_confidence: 0,
      auto_imported_low_confidence: 0,
      auto_imported_display_only: 0,
      failed_or_missing: 0,
      intentionally_excluded: 0,
    },
  );
}
